import options from "../../options";
import {mapGetters} from "vuex";
import axios from "@/services/axios.config";
import {
  isKonekoAccessControlUser,
  konekoAAdminUsernames,
  getKonekoProjectBudgetBand,
  formatProjectBudgetStatus,
  bandToDecimals,
  orgBudgetBandIsConfigured,
  isKonekoBudgetBarOverrun,
  konekoBudgetStatusTextClass,
  getMemberProjectLimitBand,
  konekoReportingElsewhereUsernames,
  resolveMemberTargets,
  resolveMemberTargetsFromConfig,
  readMemberTargetsLocalDraft,
  writeMemberTargetsLocalDraft,
  clearMemberTargetsLocalDraft,
  readMemberTargetsHiddenProjects,
  writeMemberTargetsHiddenProjects,
  clearMemberTargetsHiddenProjects,
  normalizeTargetBandForEditor,
  normalizeBudgetHhmmInput,
  canonicalizeKonekoUsername,
  canonicalizeKonekoProjectName,
  resolveCanonicalProjectDisplayNames,
  loadKonekoProjectNameAliasesFromStorage,
  konekoAMonthlyProjectBudgets,
  konekoATargetsByMonth,
  konekoMemberRowMatchesLookup,
  konekoPlanningKeyMatchesMemberRow,
  formatKonekoAPlanningMemberSelectLabel,
  resolveKonekoAPlanningUsernameKey,
  listKonekoAPlanningMemberKeys,
  listKonekoAPlanningMemberKeysForBudget,
  isKonekoMemberExcludedForMonth,
  buildKonekoTeamRosterMetaByUsername,
  konekoABudgetBackendEnabled,
  konekoABudgetBackendAllowlist,
  konekoABudgetAdminUsernames,
  isKonekoBudgetBackendEnabledForUser,
  memberTargetsSavedInApi,
  formatKonekoSubmissionLabel,
  submissionFromCache,
  profileUsernameFromJwt,
  profileDisplayNameFromJwt,
  effectiveTimesheetMonthKey,
  isFutureKonekoCalendarMonthKey,
  formatKonekoMonthDisplayTitle,
  shiftKonekoMonthKey,
  currentKonekoCalendarMonthKey,
  defaultKonekoCalendarMonthKey,
} from "@/config/konekoAConfig";
import {
  saveMyMonthTargets,
  saveRosterMemberMonthTargets,
  fetchMyMonthTargets,
  fetchRosterMonthTargets,
  fetchOrgMonthBudgets,
  saveOrgMonthBudgets,
} from "@/services/konekoABudgetApi";
import {updateKonekoTimesheetEntry} from "@/services/konekoTimesheetApi";
import {
  appendKonekoEditedByNote,
  formatKonekoEditorLabel,
  getKonekoTimesheetEditAttribution,
  parseEditedByFromNotes,
  recordKonekoTimesheetEditAttribution,
} from "@/utils/konekoTimesheetEditAttribution";
import {convertHMSFormatInputToHours, HHMMFormat} from "@/Utilites/timerHelper";
import {
  bumpKonekoVisibilityOverrides,
  dashboardViewAsState,
} from "@/utils/dashboardViewAsSync";
import {getKonekoCapabilities, KONEKO_CAP} from "@/config/konekoVisibility";
import {
  KONEKO_SORT_BUDGET_DELTA,
  KONEKO_SORT_NAME,
  KONEKO_SORT_TIME,
  STORAGE_BUDGET_EDITOR_SORT,
  STORAGE_CROSS_COMPARISON_SORT,
  STORAGE_MEMBER_BREAKDOWN_SORT,
  localeNameSort,
  readKonekoSortPref,
  sortBudgetEditorRows,
  sortCrossComparisonRows,
  sortVisibleMembers,
  writeKonekoSortPref,
} from "@/utils/konekoASort";

function konekoBudgetApiErrorMessage(err) {
  const data = err && err.response && err.response.data;
  return (
    (data && (data.error || data.detail)) ||
    (err && err.message) ||
    "Budget API request failed"
  );
}
function hoursToDecimal(value) {
  if (value == null || value === "") return 0;
  const s = String(value).trim();
  if (!s) return 0;
  if (s.includes(":")) {
    const parts = s.split(":");
    const h = parseInt(parts[0], 10);
    const m = parts.length > 1 ? parseInt(parts[1], 10) : 0;
    const hh = Number.isFinite(h) ? h : 0;
    const mm = Number.isFinite(m) ? m : 0;
    return hh + mm / 60;
  }
  if (/^\d+$/.test(s)) {
    return parseInt(s, 10);
  }
  const n = parseFloat(s.replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function decimalToHHMM(hoursDecimal) {
  const safe = Number.isFinite(hoursDecimal) ? Math.max(hoursDecimal, 0) : 0;
  const totalMinutes = Math.round(safe * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

/** Monthly hours per 1 FTE for budget editor tallies (min/max column sums). */
const KONEKO_A_BUDGET_FTE_HOURS = 150;

/** HH:MM only — team personal budgets (no FTE suffix). */
function formatTeamPersonalBudgetTally(hoursDecimal) {
  const safe = Number.isFinite(hoursDecimal) ? Math.max(hoursDecimal, 0) : 0;
  return decimalToHHMM(safe);
}

function formatBudgetEditorTallyWithFte(hoursDecimal) {
  const safe = Number.isFinite(hoursDecimal) ? Math.max(hoursDecimal, 0) : 0;
  const hoursLabel =
    Math.abs(safe - Math.round(safe)) < 0.01
      ? String(Math.round(safe))
      : safe.toFixed(1);
  const fteRaw = safe / KONEKO_A_BUDGET_FTE_HOURS;
  const fteRounded = Math.round(fteRaw * 10) / 10;
  const fteStr =
    Math.abs(fteRounded - Math.round(fteRounded)) < 0.05
      ? String(Math.round(fteRounded))
      : fteRounded.toFixed(1);
  const fteWord = Math.abs(parseFloat(fteStr) - 1) < 0.01 ? "FTE" : "FTEs";
  return `${hoursLabel} (${fteStr} ${fteWord})`;
}

function formatHoursDecimalAsFte(hoursDecimal) {
  const safe = Number.isFinite(hoursDecimal) ? Math.max(hoursDecimal, 0) : 0;
  const fteRaw = safe / KONEKO_A_BUDGET_FTE_HOURS;
  const fteRounded = Math.round(fteRaw * 10) / 10;
  if (Math.abs(fteRounded - Math.round(fteRounded)) < 0.05) {
    return String(Math.round(fteRounded));
  }
  return fteRounded.toFixed(1);
}

function listRollingMonthKeys(count, anchorDate = new Date()) {
  const anchor = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1);
  const keys = [];
  for (let i = count - 1; i >= 0; i--) {
    const m = new Date(anchor.getFullYear(), anchor.getMonth() - i, 1);
    keys.push(dateToMonthKey(m));
  }
  return keys;
}

function monthDateRangeFromKey(monthKey) {
  const parsed = parseMonthKey(monthKey);
  if (!parsed) return null;
  const start = new Date(parsed.year, parsed.month - 1, 1);
  const end = new Date(parsed.year, parsed.month, 0, 23, 59, 59, 999);
  return {start, end};
}

/** Calendar-month elapsed share (0–100) for a YYYY-MM key; uses today when in-range. */
function monthElapsedPercentForKey(monthKey) {
  const range = monthDateRangeFromKey(monthKey);
  if (!range) return 0;
  const now = new Date();
  const start = new Date(range.start);
  start.setHours(0, 0, 0, 0);
  const end = range.end;
  const t = Math.min(Math.max(now.getTime(), start.getTime()), end.getTime());
  const span = end.getTime() - start.getTime();
  if (span <= 0) return 0;
  return Math.min(
    100,
    Math.max(0, Math.round(((t - start.getTime()) / span) * 100))
  );
}

/** Linear run-rate to month-end from hours logged so far and % of month elapsed. */
function predictMonthEndHoursDecimal(hoursSoFar, elapsedPercent) {
  const hours = Number.isFinite(hoursSoFar) ? Math.max(hoursSoFar, 0) : 0;
  const pct = Number.isFinite(elapsedPercent) ? elapsedPercent : 0;
  if (hours <= 0 || pct <= 0 || pct >= 100) return null;
  return (hours * 100) / pct;
}

function formatHistoricMonthLabel(monthKey) {
  const p = parseMonthKey(monthKey);
  if (!p) return monthKey;
  const names = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${names[p.month - 1]} '${String(p.year).slice(-2)}`;
}

function dateToMonthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function parseMonthKey(monthKey) {
  const m = String(monthKey || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || mo < 1 || mo > 12)
    return null;
  return {year: y, month: mo};
}

function normalizeProjectLookupKey(name) {
  return String(name || "")
    .trim()
    .normalize("NFKC")
    .replace(/\s+/g, " ");
}

function normalizeProjectLooseKey(name) {
  return normalizeProjectLookupKey(name)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/** Drop rows where both week and month display as 0:00 (also used when hydrating localStorage cache). */
function pruneAllocationsList(allocations) {
  if (!Array.isArray(allocations)) return [];
  return allocations.filter(
    (a) =>
      hoursToDecimal(a.weekLogged) > 0 ||
      hoursToDecimal(a.monthLogged) > 0 ||
      // Keep target-configured lines even when logged hours are still zero.
      !!(a && a.target)
  );
}

/** Non-admin scope: ensure signed-in user has a row even with zero timesheets this month. */
function ensureAuthenticatedMemberInPerMember(
  perMember,
  memberById,
  userProfileData
) {
  if (!userProfileData || userProfileData.user_id == null) return;
  const uid = userProfileData.user_id;
  if (perMember[uid]) return;
  const fromList = memberById[uid];
  const username = fromList
    ? fromList.username
    : canonicalizeKonekoUsername(userProfileData.username);
  const name = fromList
    ? fromList.name
    : userProfileData.full_name || userProfileData.username || `User ${uid}`;
  perMember[uid] = {
    username: username || `user_${uid}`,
    name,
    targetHours: "0:00",
    loggedHours: "0:00",
    lastLogged: "-",
    daysSinceDailyReport: "N/A",
    _monthTotal: 0,
    _alloc: {},
  };
}

/** View-as: ensure previewed roster member has a row when timesheets are scoped to them. */
function ensurePlanningMemberInPerMember(perMember, memberById, planningKey) {
  const key = String(planningKey || "").trim();
  if (!key) return;
  for (const uid of Object.keys(perMember)) {
    const row = perMember[uid];
    if (row && konekoPlanningKeyMatchesMemberRow(row.username, key)) return;
  }
  for (const m of Object.values(memberById)) {
    if (!m || m.id == null) continue;
    if (!konekoPlanningKeyMatchesMemberRow(m.username, key)) continue;
    if (perMember[m.id]) return;
    perMember[m.id] = {
      username: m.username,
      name: m.name,
      targetHours: "0:00",
      loggedHours: "0:00",
      lastLogged: "-",
      daysSinceDailyReport: "N/A",
      _monthTotal: 0,
      _alloc: {},
    };
    return;
  }
  const canon =
    resolveKonekoAPlanningUsernameKey(key) ||
    canonicalizeKonekoUsername(key) ||
    key;
  const pretty = formatKonekoAPlanningMemberSelectLabel(key) || canon;
  const stubId = `planning:${canon}`;
  if (perMember[stubId]) return;
  perMember[stubId] = {
    username: canon,
    name: pretty,
    targetHours: "0:00",
    loggedHours: "0:00",
    lastLogged: "-",
    daysSinceDailyReport: "N/A",
    _monthTotal: 0,
    _alloc: {},
  };
}

/**
 * Resolve signed-in editor identity without touching computed `visibleMembers`
 * (that computed can call back into myBudgetEditorUser → stack overflow).
 */
function resolveMyBudgetEditorIdentity(ctx) {
  const u = ctx.userProfileData;
  let username = profileUsernameFromJwt(u);
  let displayName = profileDisplayNameFromJwt(u);
  if (!username) {
    const rows = Array.isArray(ctx.membersData) ? ctx.membersData : [];
    const lookupUsername = (u && u.username) || "";
    const lookupDisplayName = (u && u.full_name) || "";
    const hasLookup = Boolean(
      String(lookupUsername || "").trim() ||
        String(lookupDisplayName || "").trim()
    );
    const found =
      hasLookup &&
      rows.find((m) =>
        konekoMemberRowMatchesLookup(
          m.username,
          m.name,
          lookupUsername,
          lookupDisplayName
        )
      );
    if (found) {
      username = canonicalizeKonekoUsername(found.username) || found.username;
      displayName = found.name || displayName;
    }
  }
  if (!username && !(u && u.user_id)) return null;
  return {
    username: username || "",
    displayName: displayName || username || "You",
    userId: u && u.user_id != null ? u.user_id : null,
  };
}

function konekoMemberExcludedForMonth(
  monthKey,
  username,
  displayName,
  rosterMeta
) {
  return isKonekoMemberExcludedForMonth(
    monthKey,
    username,
    rosterMeta,
    displayName
  );
}

function pruneMembersForDisplay(membersData, monthKey, rosterMeta) {
  if (!Array.isArray(membersData)) return [];
  const mk = String(monthKey || "").trim();
  const rosterCanonSet = new Set(
    listKonekoAPlanningMemberKeysForBudget(mk, rosterMeta)
      .map((k) => canonicalizeKonekoUsername(k))
      .filter(Boolean)
  );
  return membersData
    .map((m) => {
      const allocations = pruneAllocationsList(m.allocations).sort(
        (a, b) => hoursToDecimal(b.monthLogged) - hoursToDecimal(a.monthLogged)
      );
      const targetMinSumDec = allocations.reduce(
        (sum, a) =>
          sum +
          (a.personalBudgetBar && a.personalBudgetBar.low != null
            ? a.personalBudgetBar.low
            : a.target
            ? hoursToDecimal(a.target)
            : 0),
        0
      );
      const targetMaxSumDec = allocations.reduce(
        (sum, a) =>
          sum +
          (a.personalBudgetBar && a.personalBudgetBar.high != null
            ? a.personalBudgetBar.high
            : a.target
            ? hoursToDecimal(a.target)
            : 0),
        0
      );
      return {
        ...m,
        allocations,
        targetMinHours: decimalToHHMM(targetMinSumDec),
        targetMaxHours: decimalToHHMM(targetMaxSumDec),
        targetHours: decimalToHHMM(targetMaxSumDec),
      };
    })
    .filter((m) => {
      if (konekoMemberExcludedForMonth(mk, m.username, m.name, rosterMeta)) {
        return false;
      }
      const canon = canonicalizeKonekoUsername(m.username);
      const onPlanningRoster = Boolean(canon && rosterCanonSet.has(canon));
      return (
        m.allocations.length > 0 ||
        hoursToDecimal(m.targetHours) > 0 ||
        konekoReportingElsewhereUsernames.includes(canon) ||
        onPlanningRoster
      );
    });
}

function pruneProjectsForDisplay(projectBudgetsData) {
  if (!Array.isArray(projectBudgetsData)) return [];
  return projectBudgetsData.filter(
    (p) => hoursToDecimal(p.weekLogged) > 0 || hoursToDecimal(p.monthLogged) > 0
  );
}

function projectMonthLoggedDecimal(membersData, projectName) {
  const members = Array.isArray(membersData) ? membersData : [];
  let sum = 0;
  for (const m of members) {
    const line = findMemberAllocationLine(m, projectName);
    if (line) sum += hoursToDecimal(line.monthLogged);
  }
  return sum;
}

function memberDeclaredEstimateDec(band) {
  if (!band) return 0;
  const targetDec = band.target != null ? hoursToDecimal(band.target) : 0;
  const highDec = band.high != null ? hoursToDecimal(band.high) : 0;
  const lowDec = band.low != null ? hoursToDecimal(band.low) : 0;
  const estimateDec =
    targetDec > 0 ? targetDec : highDec > 0 ? highDec : lowDec;
  return estimateDec > 0 ? estimateDec : 0;
}

/** Rich row for snapshot / cross-comparison member drill-down. */
function buildMemberProjectContributorRow(
  member,
  projectName,
  monthKey,
  targetCache,
  monthElapsedPercent,
  rosterMeta = null
) {
  if (
    konekoMemberExcludedForMonth(
      monthKey,
      member.username,
      member.name,
      rosterMeta
    )
  ) {
    return null;
  }
  const line = findMemberAllocationLine(member, projectName);
  const wDec = line ? hoursToDecimal(line.weekLogged) : 0;
  const moDec = line ? hoursToDecimal(line.monthLogged) : 0;
  const personalBand = getMemberProjectLimitBand(
    monthKey,
    member.username,
    projectName,
    member.name,
    targetCache,
    rosterMeta
  );
  const declaredDec = memberDeclaredEstimateDec(personalBand);
  if (wDec <= 0 && moDec <= 0 && declaredDec <= 0) return null;

  const parsedBand = personalBand ? bandToDecimals(personalBand) : null;
  const target =
    personalBand &&
    (personalBand.target || personalBand.high || personalBand.low)
      ? String(personalBand.target || personalBand.high || personalBand.low)
      : null;
  let percent = null;
  if (target && moDec > 0) {
    const targetDec = hoursToDecimal(target);
    if (targetDec > 0) {
      percent = `${Math.round((moDec / targetDec) * 100)}%`;
    }
  }
  const personalBudgetBar = {
    current: moDec,
    low: parsedBand ? parsedBand.low : null,
    high: parsedBand ? parsedBand.high : null,
    target:
      parsedBand && parsedBand.target != null
        ? parsedBand.target
        : target && hoursToDecimal(target) > 0
        ? hoursToDecimal(target)
        : null,
  };
  const budgetFlex = memberPersonalBudgetIsFlex(personalBudgetBar);

  return {
    name: member.name || member.username,
    username: member.username,
    declaredDec,
    declaredHours: declaredDec > 0 ? decimalToHHMM(declaredDec) : "—",
    weekLogged: line ? line.weekLogged : "0:00",
    monthLogged: line ? line.monthLogged : "0:00",
    personalBudgetBar,
    budgetFlex,
    percent,
    target,
    entries: line && Array.isArray(line.entries) ? line.entries : [],
    barStatus: memberBudgetBarStatus(personalBudgetBar, monthElapsedPercent),
  };
}

function contributorCanonKey(row) {
  const fromUser = resolveKonekoAPlanningUsernameKey(row.username);
  if (fromUser) return fromUser;
  const fromName = resolveKonekoAPlanningUsernameKey(row.name);
  if (fromName) return fromName;
  const canonUser = canonicalizeKonekoUsername(row.username);
  if (canonUser) return canonUser;
  const canonName = canonicalizeKonekoUsername(row.name);
  if (canonName) return canonName;
  return String(row.username || row.name || "")
    .trim()
    .toLowerCase();
}

/** One row per person — merges reegesh/regesh and duplicate member records. */
function mergeContributorRowsByPerson(rows, monthElapsedPercent) {
  const byCanon = new Map();
  for (const row of rows) {
    const canon = contributorCanonKey(row);
    if (!canon) continue;
    const existing = byCanon.get(canon);
    if (!existing) {
      byCanon.set(canon, {
        ...row,
        username: canon,
        name:
          formatKonekoAPlanningMemberSelectLabel(canon) ||
          row.name ||
          row.username,
      });
      continue;
    }
    const wSum =
      hoursToDecimal(existing.weekLogged) + hoursToDecimal(row.weekLogged);
    const mSum =
      hoursToDecimal(existing.monthLogged) + hoursToDecimal(row.monthLogged);
    existing.weekLogged = decimalToHHMM(wSum);
    existing.monthLogged = decimalToHHMM(mSum);
    existing.declaredDec = Math.max(
      existing.declaredDec || 0,
      row.declaredDec || 0
    );
    existing.declaredHours =
      existing.declaredDec > 0 ? decimalToHHMM(existing.declaredDec) : "—";
    const mergedEntries = [
      ...(existing.entries || []),
      ...(row.entries || []),
    ].sort((a, b) => {
      const ta = a.date ? new Date(a.date).getTime() : 0;
      const tb = b.date ? new Date(b.date).getTime() : 0;
      return tb - ta;
    });
    existing.entries = mergedEntries;
    if (existing.personalBudgetBar) {
      existing.personalBudgetBar = {
        ...existing.personalBudgetBar,
        current: mSum,
      };
    }
    existing.budgetFlex =
      memberPersonalBudgetIsFlex(existing.personalBudgetBar) || row.budgetFlex;
    existing.barStatus = memberBudgetBarStatus(
      existing.personalBudgetBar,
      monthElapsedPercent
    );
    if (hoursToDecimal(row.monthLogged) > 0 && row.name) {
      existing.name = row.name;
    }
  }
  return Array.from(byCanon.values());
}

/** One pass over members — avoids O(projects × members) contributor rebuilds in Cross Comparison. */
function buildAllMonthContributorsByProject(
  membersData,
  monthKey,
  targetCache,
  monthElapsedPercent,
  rosterMeta = null
) {
  const members = Array.isArray(membersData) ? membersData : [];
  const rowsByProject = Object.create(null);
  const memberTargetsCache = new Map();

  const targetsForMember = (member) => {
    const cacheKey =
      canonicalizeKonekoUsername(member.username) ||
      String(member.username || "")
        .trim()
        .toLowerCase();
    if (!cacheKey) return {};
    if (memberTargetsCache.has(cacheKey)) {
      return memberTargetsCache.get(cacheKey);
    }
    const targets = resolveMemberTargets(
      monthKey,
      member.username,
      member.name,
      targetCache,
      rosterMeta
    );
    memberTargetsCache.set(cacheKey, targets);
    return targets;
  };

  const projectsForMember = (member) => {
    const projects = new Set();
    (member.allocations || []).forEach((line) => {
      if (line && line.project) projects.add(line.project);
    });
    Object.keys(targetsForMember(member) || {}).forEach((p) => projects.add(p));
    return projects;
  };

  for (const m of members) {
    if (
      konekoMemberExcludedForMonth(monthKey, m.username, m.name, rosterMeta)
    ) {
      continue;
    }
    for (const projectName of projectsForMember(m)) {
      const row = buildMemberProjectContributorRow(
        m,
        projectName,
        monthKey,
        targetCache,
        monthElapsedPercent,
        rosterMeta
      );
      if (!row) continue;
      if (!rowsByProject[projectName]) rowsByProject[projectName] = [];
      rowsByProject[projectName].push(row);
    }
  }

  const out = Object.create(null);
  for (const projectName of Object.keys(rowsByProject)) {
    const merged = mergeContributorRowsByPerson(
      rowsByProject[projectName],
      monthElapsedPercent
    ).filter(
      (row) =>
        !isKonekoMemberExcludedForMonth(
          monthKey,
          row.username,
          rosterMeta,
          row.name
        )
    );
    merged.sort(
      (a, b) =>
        hoursToDecimal(b.monthLogged) - hoursToDecimal(a.monthLogged) ||
        (b.declaredDec || 0) - (a.declaredDec || 0)
    );
    out[projectName] = merged;
  }
  return out;
}

function normalizeFkId(value) {
  if (value == null || value === "") return null;
  if (typeof value === "object" && value.id != null) return value.id;
  const n = Number(value);
  return Number.isFinite(n) ? n : value;
}

/** Timesheet list API may return a bare array or a paginated envelope. */
function parseTimesheetApiList(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    if (Array.isArray(data.results)) return data.results;
    if (Array.isArray(data.data)) return data.data;
  }
  return [];
}

/** Django User id for timesheet `user_id` filter (not Member.id). */
function resolveTeamRowUserId(row) {
  if (!row || typeof row !== "object") return null;
  const nested = row.user;
  if (nested != null && typeof nested === "object" && !Array.isArray(nested)) {
    const id = Number(nested.id);
    if (Number.isFinite(id) && id > 0) return id;
  }
  if (typeof nested === "number" && Number.isFinite(nested) && nested > 0) {
    return nested;
  }
  const explicit = Number(row.user_id);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  return null;
}

/** Align timer log rows with monthly timesheet shape for buildRealData. */
function normalizeTimerRowToTimesheetRow(row) {
  if (!row || typeof row !== "object") return row;
  const logDate = preserveLogDateString(
    row.log_date || row.timer_log_date || row.created_at
  );
  const hours =
    row.working_hours ?? row.hours_with_timer ?? row.hours ?? row.log_hour ?? 0;
  return {
    ...row,
    log_date: logDate || row.log_date,
    timer_log_date: logDate || row.timer_log_date,
    working_hours: hours,
    hours,
    hours_with_timer:
      row.hours_with_timer != null ? row.hours_with_timer : hours,
  };
}

function timesheetRowInIsoRange(row, monthStartIso, monthEndIso) {
  const iso = preserveLogDateString(
    row.log_date || row.timer_log_date || row.created_at
  );
  if (!iso) return false;
  return iso >= monthStartIso && iso <= monthEndIso;
}

function dedupeTimesheetRows(rows) {
  const byId = new Map();
  const out = [];
  for (const row of rows || []) {
    const id = Number(row && row.id);
    if (Number.isFinite(id) && id > 0) {
      if (!byId.has(id)) {
        byId.set(id, row);
        out.push(row);
      }
    } else {
      out.push(row);
    }
  }
  return out;
}

function preserveLogDateString(value) {
  if (value == null || value === "") return null;
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return null;
}

function timesheetRowNotes(row) {
  const raw =
    row.task_description ||
    row.log_description ||
    row.description ||
    row.notes ||
    "";
  const text = String(raw || "")
    .trim()
    .replace(/\[Koneko edited by [^\]]+\]/gi, "")
    .trim();
  return text || "—";
}

function timesheetRowEditorFromApi(row) {
  if (!row || typeof row !== "object") return null;
  const username =
    row.edited_by_username ||
    row.updated_by_username ||
    row.modified_by_username ||
    row.last_edited_by_username ||
    (row.edited_by && row.edited_by.username) ||
    (row.updated_by && row.updated_by.username) ||
    "";
  const key = canonicalizeKonekoUsername(username);
  if (!key) return null;
  const displayName =
    row.edited_by_full_name ||
    row.updated_by_full_name ||
    (row.edited_by && row.edited_by.full_name) ||
    (row.updated_by && row.updated_by.full_name) ||
    key;
  return {
    editorUsername: key,
    editorDisplayName: displayName,
    editorLabel: formatKonekoEditorLabel(key, displayName),
    fromApi: true,
  };
}

function summarizeMemberTimesheetEdits(projects) {
  const editors = new Set();
  let count = 0;
  (projects || []).forEach((proj) => {
    (proj.entries || []).forEach((entry) => {
      if (!entry.editedByLabel || entry.editedBySelf) return;
      count += 1;
      editors.add(entry.editedByLabel);
    });
  });
  if (!count) {
    return {count: 0, editors: [], chipLabel: ""};
  }
  const names = Array.from(editors);
  const chipLabel =
    names.length === 1
      ? `Hours edited by ${names[0]}`
      : `Hours edited (${count} lines)`;
  return {count, editors: names, chipLabel};
}

function memberPersonalBudgetIsFlex(bar) {
  if (!bar) return true;
  const lo = bar.low != null && bar.low > 0;
  const hi = bar.high != null && bar.high > 0;
  const tgt = bar.target != null && bar.target > 0;
  return !lo && !hi && !tgt;
}

function findMemberAllocationLine(member, projectName) {
  const needle = normalizeProjectLookupKey(projectName);
  if (!needle) return null;
  const needleLoose = normalizeProjectLooseKey(projectName);
  const canonNeedle = canonicalizeKonekoProjectName(projectName) || needle;
  const list = Array.isArray(member.allocations) ? member.allocations : [];
  const exact = list.find(
    (a) => normalizeProjectLookupKey(a.project) === needle
  );
  if (exact) return exact;
  const needleLower = needle.toLowerCase();
  const ci = list.find(
    (a) => normalizeProjectLookupKey(a.project).toLowerCase() === needleLower
  );
  if (ci) return ci;
  const loose = list.find(
    (a) => normalizeProjectLooseKey(a.project) === needleLoose
  );
  if (loose) return loose;
  return (
    list.find(
      (a) =>
        (canonicalizeKonekoProjectName(a.project) ||
          normalizeProjectLookupKey(a.project)) === canonNeedle
    ) || null
  );
}

function findMemberAllocBucket(alloc, projectName) {
  if (!alloc || typeof alloc !== "object") return null;
  if (alloc[projectName]) return alloc[projectName];
  const canonNeedle =
    canonicalizeKonekoProjectName(projectName) ||
    normalizeProjectLookupKey(projectName);
  for (const [key, bucket] of Object.entries(alloc)) {
    const keyCanon =
      canonicalizeKonekoProjectName(key) || normalizeProjectLookupKey(key);
    if (keyCanon === canonNeedle) return bucket;
  }
  return null;
}

function mergeEditorTargetBandForProject(fromConfig, draft, projectName) {
  const canonNeedle =
    canonicalizeKonekoProjectName(projectName) ||
    normalizeProjectLookupKey(projectName);
  let band = {};
  const absorb = (section) => {
    if (!section || typeof section !== "object") return;
    Object.entries(section).forEach(([key, value]) => {
      const keyCanon =
        canonicalizeKonekoProjectName(key) || normalizeProjectLookupKey(key);
      if (keyCanon !== canonNeedle) return;
      band = {...band, ...normalizeTargetBandForEditor(value)};
    });
  };
  absorb(fromConfig);
  absorb(draft);
  return band;
}

/**
 * Synthetic user ids for planning-roster people missing from the team-members API.
 * Negative range avoids collision with real NekoHR user ids.
 */
const KONEKO_ROSTER_SYNTHETIC_ID_BASE = -918273600;

function sumSnapshotLoggedForCanon(byName, canon) {
  let weekLogged = 0;
  let monthLogged = 0;
  for (const raw of Object.keys(byName || {})) {
    const rawCanon =
      canonicalizeKonekoProjectName(raw) || normalizeProjectLookupKey(raw);
    if (rawCanon !== canon) continue;
    const p = byName[raw];
    if (p) {
      weekLogged += hoursToDecimal(p.weekLogged);
      monthLogged += hoursToDecimal(p.monthLogged);
    }
  }
  return {weekLogged, monthLogged};
}

function rosterMemberRepresentedInPerMember(perMember, rosterKey) {
  const rows = Object.values(perMember || {});
  for (const row of rows) {
    if (!row) continue;
    const u = row.username;
    const n = row.name;
    if (
      canonicalizeKonekoUsername(u) === canonicalizeKonekoUsername(rosterKey)
    ) {
      return true;
    }
    if (konekoPlanningKeyMatchesMemberRow(u, rosterKey)) {
      return true;
    }
    const pretty = formatKonekoAPlanningMemberSelectLabel(rosterKey);
    if (konekoMemberRowMatchesLookup(u, n, rosterKey, pretty)) {
      return true;
    }
  }
  return false;
}

/**
 * Add zero-hour rows for each `konekoATargets` roster key not represented by any team row,
 * so View-as / ?member= can resolve people who are budgeted but missing from team-members.
 */
function injectMissingPlanningRosterMembers(perMember, monthKey, rosterMeta) {
  if (!perMember || typeof perMember !== "object") return;
  let seq = 0;
  for (const rk of listKonekoAPlanningMemberKeysForBudget(
    monthKey,
    rosterMeta
  )) {
    if (rosterMemberRepresentedInPerMember(perMember, rk)) continue;
    const id = KONEKO_ROSTER_SYNTHETIC_ID_BASE - seq;
    seq += 1;
    perMember[id] = {
      username: String(rk).trim().toLowerCase(),
      name: formatKonekoAPlanningMemberSelectLabel(rk),
      targetHours: "0:00",
      loggedHours: "0:00",
      lastLogged: "—",
      daysSinceDailyReport: "N/A",
      _monthTotal: 0,
      _alloc: {},
    };
  }
}

const STORAGE_PROJECT_SNAPSHOT_BASELINE_LEGACY =
  "konekoA:projectSnapshotBudgetBaseline";
const STORAGE_PROJECT_SNAPSHOT_BASELINE_BY_PROJECT =
  "konekoA:projectSnapshotBudgetBaselineByProject";
/** Month-agnostic Neko project names for My budget Add project (survives future-month loads). */
const STORAGE_KONEKO_PROJECTS_CATALOG = "konekoA:projectsCatalog:v1";
const STORAGE_KONEKO_SELECTED_MONTH = "konekoA:selectedMonthKey:v1";

function projectCanonKey(name) {
  return canonicalizeKonekoProjectName(name) || normalizeProjectLookupKey(name);
}

/** Shallow-merge project maps but merge low/high/target per project (not whole-band replace). */
function mergeMonthlyBudgetBandMaps(...maps) {
  const out = {};
  for (const map of maps) {
    for (const [project, band] of Object.entries(map || {})) {
      if (!project) continue;
      out[project] = {
        ...normalizeTargetBandForEditor(out[project]),
        ...normalizeTargetBandForEditor(band),
      };
    }
  }
  return out;
}

/** Map budget draft keys to canonical NekoHR project_name (merge alias duplicates). */
function normalizeBudgetProjectDraftMap(draft, catalogNames = []) {
  const catalog = Array.isArray(catalogNames)
    ? catalogNames.map((n) => String(n || "").trim()).filter(Boolean)
    : [];
  const merged = {};
  for (const [rawKey, band] of Object.entries(draft || {})) {
    if (!rawKey) continue;
    const canon = projectCanonKey(rawKey);
    let saveKey =
      resolveCanonicalProjectDisplayNames([rawKey])[0] || String(rawKey).trim();
    const catalogMatch = catalog.find((n) => projectCanonKey(n) === canon);
    if (catalogMatch) saveKey = catalogMatch;
    merged[saveKey] = {
      ...normalizeTargetBandForEditor(merged[saveKey]),
      ...normalizeTargetBandForEditor(band),
    };
  }
  return merged;
}

function persistKonekoSelectedMonthKey(monthKey) {
  if (!parseMonthKey(monthKey)) return;
  try {
    sessionStorage.setItem(STORAGE_KONEKO_SELECTED_MONTH, monthKey);
  } catch {
    /* ignore */
  }
}

function readKonekoSelectedMonthKey() {
  try {
    const raw = String(
      sessionStorage.getItem(STORAGE_KONEKO_SELECTED_MONTH) || ""
    ).trim();
    return parseMonthKey(raw) ? raw : "";
  } catch {
    return "";
  }
}

function normalizeKonekoProjectsApiRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((p) => ({
      id: p.id,
      project_name: String(p.project_name || p.name || "").trim(),
      name: String(p.project_name || p.name || "").trim(),
    }))
    .filter((p) => p.id && p.name);
}

function readKonekoProjectsCatalogFromSession() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KONEKO_PROJECTS_CATALOG);
    if (!raw) return [];
    return normalizeKonekoProjectsApiRows(JSON.parse(raw));
  } catch {
    return [];
  }
}

function writeKonekoProjectsCatalogToSession(rows) {
  try {
    const normalized = normalizeKonekoProjectsApiRows(rows);
    if (!normalized.length) return;
    sessionStorage.setItem(
      STORAGE_KONEKO_PROJECTS_CATALOG,
      JSON.stringify(normalized)
    );
  } catch {
    /* ignore */
  }
}

/**
 * Effective budget cap for snapshot % / delta (respects low/high baseline toggle).
 * @returns {{ denom: number|null, suffix: string }}
 */
function snapshotBudgetDenominator(bar, baselinePreference) {
  if (!bar) return {denom: null, suffix: ""};
  const low = bar.low != null ? Number(bar.low) : null;
  const high = bar.high != null ? Number(bar.high) : null;
  const tgt = bar.target != null ? Number(bar.target) : null;
  const lowPos = low != null && low > 0 ? low : null;
  const highPos = high != null && high > 0 ? high : null;
  const targetPos = tgt != null && tgt > 0 ? tgt : null;
  const eps = 1e-6;
  const sameBand =
    lowPos != null && highPos != null && Math.abs(lowPos - highPos) <= eps;

  let denom = null;
  let suffix = "budget cap";

  if (sameBand) {
    denom = lowPos;
    suffix = "budget cap";
  } else if (!lowPos && !highPos && targetPos) {
    denom = targetPos;
    suffix = "budget target";
  } else if (lowPos && !highPos) {
    denom = lowPos;
    suffix = "budget cap";
  } else if (!lowPos && highPos) {
    denom = highPos;
    suffix = "budget cap";
  } else if (lowPos && highPos) {
    const useHigh = baselinePreference === "high";
    if (useHigh) {
      denom = highPos ?? targetPos ?? lowPos;
      suffix =
        denom === highPos
          ? "high cap"
          : denom === targetPos
          ? "budget target"
          : "low cap";
    } else {
      denom = lowPos ?? targetPos ?? highPos;
      suffix =
        denom === lowPos
          ? "low cap"
          : denom === targetPos
          ? "budget target"
          : "high cap";
    }
  } else {
    denom = lowPos ?? highPos ?? targetPos;
    if (denom === targetPos) suffix = "budget target";
    else suffix = "budget cap";
  }

  if (denom == null || denom <= 0) return {denom: null, suffix: ""};
  return {denom, suffix};
}

/** Month hours minus effective cap; positive = over budget. */
function computeSnapshotBudgetDeltaHours(project, baselinePreference) {
  const bar = project && project.budgetBar;
  if (!bar) return null;
  const current = Number(bar.current);
  if (!Number.isFinite(current) || current < 0) return null;
  const {denom} = snapshotBudgetDenominator(bar, baselinePreference);
  if (denom == null || denom <= 0) return null;
  return current - denom;
}

/**
 * Hours logged above the linear month-pace line, measured against the SAME
 * axis the bar renders on (0 → scaleMax). The dark-red "pace" line sits at
 * `monthElapsedPercent`% of the track width, so the number now matches the
 * visible gap between the dot and that line (was measured vs the cap before,
 * which over-stated how far "ahead of pace" a project looked).
 *
 * `baselinePreference` is kept for call-site compatibility but no longer used:
 * the pace line is independent of the Low/High baseline toggle.
 */
function computeSnapshotPaceAheadHours(
  project,
  baselinePreference,
  monthElapsedPercent
) {
  const bar = project && project.budgetBar;
  if (!bar) return null;
  const current = Number(bar.current);
  if (!Number.isFinite(current) || current < 0) return null;
  const mp = monthElapsedPercent;
  if (typeof mp !== "number" || !Number.isFinite(mp) || mp < 0 || mp > 100) {
    return null;
  }
  const scaleMax = budgetBarScaleMax(bar);
  if (scaleMax == null || scaleMax <= 0) return null;
  const paceLineHours = scaleMax * (mp / 100);
  const ahead = current - paceLineHours;
  return ahead > 0.02 ? ahead : 0;
}

/** True when org bar has two different positive bounds (usage % baseline toggle applies). */
function projectBudgetBarHasDistinctLowHigh(bar) {
  if (!bar) return false;
  const low = bar.low != null ? Number(bar.low) : null;
  const high = bar.high != null ? Number(bar.high) : null;
  const lowPos = low != null && low > 0 ? low : null;
  const highPos = high != null && high > 0 ? high : null;
  if (!lowPos || !highPos) return false;
  return Math.abs(lowPos - highPos) > 1e-6;
}

/** Track upper bound (hours), mirrors `KonekoBudgetRangeBar` `scaleMax`. */
function budgetBarScaleMax(bar) {
  if (!bar) return null;
  const parts = [bar.current, bar.low, bar.high, bar.target].filter(
    (x) => x != null && Number.isFinite(x) && x >= 0
  );
  const peak = parts.length ? Math.max(...parts) : 0;
  return peak <= 0 ? 1 : peak * 1.12;
}

/** Blue-dot horizontal position (% of track), same formula as `KonekoBudgetRangeBar` `scaleMax`. */
function budgetBarCurrentPositionPct(bar) {
  if (!bar) return null;
  const current = Number(bar.current);
  if (!Number.isFinite(current) || current < 0) return null;
  const scaleMax = budgetBarScaleMax(bar);
  if (scaleMax == null || scaleMax <= 0) return null;
  return Math.min(100, Math.max(0, (current / scaleMax) * 100));
}

/**
 * Bar health, worst-first:
 *  - "over"  : red    — past the hard cap (high / single bound / target)
 *  - "pace"  : amber  — ahead of the calendar pace line (and under the cap)
 *  - "good"  : green  — healthy: has a budget, has logged time, is at/above the
 *              low floor, under the cap, and on/behind the pace line
 *  - "ok"    : neutral — anything else (e.g. quiet / still under the low floor)
 */
function memberBudgetBarStatus(bar, monthElapsedPercent) {
  if (!bar) return "ok";
  if (isKonekoBudgetBarOverrun(bar.current, bar)) return "over";
  const mp = monthElapsedPercent;
  const barPos = budgetBarCurrentPositionPct(bar);
  if (
    typeof mp === "number" &&
    Number.isFinite(mp) &&
    mp >= 0 &&
    mp <= 100 &&
    barPos != null &&
    barPos > mp
  ) {
    return "pace";
  }
  const current = Number(bar.current);
  if (Number.isFinite(current) && current > 0) {
    const low = bar.low != null && bar.low > 0 ? bar.low : null;
    const high = bar.high != null && bar.high > 0 ? bar.high : null;
    const target = bar.target != null && bar.target > 0 ? bar.target : null;
    const hasBudget = low != null || high != null || target != null;
    const aboveLowFloor = low == null || current >= low;
    if (hasBudget && aboveLowFloor) return "good";
  }
  return "ok";
}

export default {
  data() {
    return {
      loading: false,
      loadError: "",
      membersData: [],
      projectBudgetsData: [],
      lastTimesheetRowCount: null,
      lastTimesheetFetchDebug: null,
      lastUpdatedAt: "",
      selectedMonthKey: "",
      monthBudgetDraft: {},
      /** Project keys edited in org Budget Editor this session (before Save). */
      monthBudgetDirtyProjects: {},
      monthBudgetSaving: false,
      orgBudgetAutoSaveTimer: null,
      konekoBudgetSaveOk: "",
      /** Local personal caps for the logged-in user (`project` → `{ low, high }`). */
      myMemberTargetsDraft: {},
      /** Projects removed from My budget for this month (persisted in browser). */
      myMemberTargetsHiddenProjects: [],
      dashboardEmbedFlag: false,
      /** Per project name (normalized display key) → `low` | `high` for distinct org caps. */
      projectSnapshotBudgetBaselineByProject: {},
      /** Migrated from legacy single global key when per-project map was absent. */
      legacyProjectSnapshotBudgetBaseline: null,
      /** YYYY-MM → org-wide logged hours (decimal) for historic FTE row. */
      historicFteByMonth: {},
      historicFteLoading: false,
      historicFteError: "",
      historicFteLoadToken: 0,
      myBudgetNewProjectName: "",
      konekoBudgetApiError: "",
      konekoBudgetApiLoading: false,
      /** Admin team-budget inline edit: { memberKey, project, field }. */
      teamBudgetEdit: null,
      teamBudgetEditValue: "",
      teamBudgetSaving: false,
      /** `${username}::${project}` for Member Breakdown project drill-down. */
      memberBreakdownProjectExpand: null,
      /** `${memberKey}::${project}` for Team personal budgets project drill-down. */
      /** `${memberKey}::${project}` keys for expanded team-budget project rows. */
      teamBudgetProjectExpandKeys: [],
      /** Reactive copy of roster month-targets API (monthKey -> username -> projects). */
      konekoMemberTargetsApiCacheByMonth: {},
      /** Reactive copy of org month-budgets API (monthKey -> project -> band). */
      konekoOrgBudgetsApiCacheByMonth: {},
      /** Reactive submission metadata (monthKey -> username -> { count, ... }). */
      konekoMemberSubmissionsApiCacheByMonth: {},
      /** From team-members API: roster key → { koneko_excluded_from_month, is_currently_working }. */
      konekoTeamRosterMetaByUsername: {},
      memberBreakdownSortBy: readKonekoSortPref(
        STORAGE_MEMBER_BREAKDOWN_SORT,
        [KONEKO_SORT_NAME, KONEKO_SORT_TIME],
        KONEKO_SORT_NAME
      ),
      crossComparisonSortBy: readKonekoSortPref(
        STORAGE_CROSS_COMPARISON_SORT,
        [KONEKO_SORT_NAME, KONEKO_SORT_BUDGET_DELTA],
        KONEKO_SORT_BUDGET_DELTA
      ),
      budgetEditorSortBy: readKonekoSortPref(
        STORAGE_BUDGET_EDITOR_SORT,
        [KONEKO_SORT_NAME, KONEKO_SORT_BUDGET_DELTA],
        KONEKO_SORT_BUDGET_DELTA
      ),
      /** Latest submission for the logged-in user (current month). */
      myBudgetSubmission: null,
      /** Raw API payload for rebuild without full network reload. */
      konekoWorkloadCache: null,
      konekoProjectsList: [],
      timesheetReassignSavingId: null,
      timesheetHoursSavingId: null,
      timesheetHoursEdit: {
        timesheetId: null,
        value: "",
      },
      konekoReassignNotice: "",
      konekoReassignConfirm: {
        show: false,
        entry: null,
        newProjectId: null,
        newProjectName: "",
        fromProject: "",
        memberKey: "",
        hoursLabel: "",
        dateLabel: "",
      },
    };
  },
  created() {
    loadKonekoProjectNameAliasesFromStorage();
    try {
      const legacy = localStorage.getItem(
        STORAGE_PROJECT_SNAPSHOT_BASELINE_LEGACY
      );
      if (legacy === "high" || legacy === "low") {
        this.legacyProjectSnapshotBudgetBaseline = legacy;
      }
      const raw = localStorage.getItem(
        STORAGE_PROJECT_SNAPSHOT_BASELINE_BY_PROJECT
      );
      if (raw) {
        const o = JSON.parse(raw);
        if (o && typeof o === "object" && !Array.isArray(o)) {
          this.projectSnapshotBudgetBaselineByProject = o;
        }
      }
    } catch {
      /* ignore */
    }
  },
  watch: {
    selectedMonthKey(newVal) {
      if (parseMonthKey(newVal)) persistKonekoSelectedMonthKey(newVal);
    },
    globalViewAsPlanningKey(newVal, oldVal) {
      if (this.dashboardEmbedFlag || newVal === oldVal) return;
      this.loadMyMemberTargetsDraft();
      this.loadKonekoAData(true);
    },
    embeddedViewAsUsername(newVal, oldVal) {
      if (!this.dashboardEmbedFlag || newVal === oldVal) return;
      this.loadMyMemberTargetsDraft();
      this.loadKonekoAData(true);
    },
    projectSnapshotBudgetBaselineByProject: {
      deep: true,
      handler() {
        try {
          localStorage.setItem(
            STORAGE_PROJECT_SNAPSHOT_BASELINE_BY_PROJECT,
            JSON.stringify(this.projectSnapshotBudgetBaselineByProject || {})
          );
        } catch {
          /* ignore */
        }
      },
    },
  },
  computed: {
    ...mapGetters(["userProfileData"]),
    projectBudgets() {
      const liveRows = Array.isArray(this.projectBudgetsData)
        ? this.projectBudgetsData
        : [];
      const budgetKeys = Object.keys(this.currentMonthlyBudgetMap);
      const byName = {};
      liveRows.forEach((p) => {
        byName[p.name] = p;
      });
      const allProjectNames = resolveCanonicalProjectDisplayNames(
        Object.keys(byName),
        budgetKeys
      );

      // Derive bars from live totals + monthly budget config.
      return allProjectNames
        .filter((name) => {
          const canon =
            canonicalizeKonekoProjectName(name) ||
            normalizeProjectLookupKey(name);
          const {weekLogged, monthLogged} = sumSnapshotLoggedForCanon(
            byName,
            canon
          );
          const band = this.getProjectBudgetBandFromMap(name);
          const hasOrg = orgBudgetBandIsConfigured(band);
          return hasOrg || weekLogged + monthLogged > 0;
        })
        .map((name) => {
          const canon =
            canonicalizeKonekoProjectName(name) ||
            normalizeProjectLookupKey(name);
          const {weekLogged, monthLogged} = sumSnapshotLoggedForCanon(
            byName,
            canon
          );
          const p = byName[name] || {
            name,
            weekLogged: decimalToHHMM(weekLogged),
            monthLogged: decimalToHHMM(monthLogged),
            budgetStatus: "FLEX",
          };
          const merged = {
            ...p,
            name,
            weekLogged: decimalToHHMM(weekLogged),
            monthLogged: decimalToHHMM(monthLogged),
          };
          const band = this.getProjectBudgetBandFromMap(name);
          const currentDec = hoursToDecimal(merged.monthLogged);
          const budgetBar = orgBudgetBandIsConfigured(band)
            ? {current: currentDec, ...bandToDecimals(band)}
            : null;
          const budgetStatus =
            merged.budgetStatus ||
            formatProjectBudgetStatus(currentDec, band) ||
            "FLEX";
          return {...merged, budgetBar, budgetStatus};
        })
        .sort((a, b) => localeNameSort(a.name, b.name));
    },
    /** Each project row + who logged time on that project (Koneko A snapshot drill-down). */
    projectBudgetSnapshotRows() {
      const mp = this.monthElapsedPercent;
      return (this.projectBudgets || []).map((p) => {
        const snapshotOverBudget = Boolean(
          p.budgetBar &&
            isKonekoBudgetBarOverrun(p.budgetBar.current, p.budgetBar)
        );
        const barPos = budgetBarCurrentPositionPct(p.budgetBar);
        const snapshotPastCalendarPaceLine =
          typeof mp === "number" &&
          Number.isFinite(mp) &&
          mp >= 0 &&
          mp <= 100 &&
          barPos != null &&
          barPos > mp;
        const snapshotRowBudgetWarning =
          snapshotOverBudget || snapshotPastCalendarPaceLine;
        const barStatus = memberBudgetBarStatus(p.budgetBar, mp);
        return {
          ...p,
          barStatus: barStatus === "ok" ? "" : barStatus,
          contributors: this.monthContributorsByProject[p.name] || [],
          snapshotShowBaselineToggle: projectBudgetBarHasDistinctLowHigh(
            p.budgetBar
          ),
          snapshotBaseline: this.getProjectSnapshotBudgetBaseline(p.name),
          snapshotUsageLabel: this.computeSnapshotUsage(p).label,
          snapshotBudgetDeltaHours: computeSnapshotBudgetDeltaHours(
            p,
            this.getProjectSnapshotBudgetBaseline(p.name)
          ),
          snapshotPaceAheadHours: computeSnapshotPaceAheadHours(
            p,
            this.getProjectSnapshotBudgetBaseline(p.name),
            mp
          ),
          snapshotOverBudget,
          snapshotPastCalendarPaceLine,
          snapshotRowBudgetWarning,
        };
      });
    },
    konekoBudgetBackendUsesApi() {
      if (!konekoABudgetBackendEnabled) return false;
      const list = konekoABudgetBackendAllowlist || [];
      if (!list.length) return Boolean(this.myBudgetEditorUser);
      const user = this.myBudgetEditorUser;
      return Boolean(
        user && isKonekoBudgetBackendEnabledForUser(user.username)
      );
    },
    signedInHasOrgBudgetEditorCap() {
      void dashboardViewAsState.visibilityOverridesRev;
      const u = canonicalizeKonekoUsername(
        this.userProfileData && this.userProfileData.username
      );
      if (!u) return false;
      return Boolean(
        getKonekoCapabilities(u)[KONEKO_CAP.konekoOrgBudgetEditor]
      );
    },
    signedInIsKonekoBudgetAdmin() {
      const u = canonicalizeKonekoUsername(
        this.userProfileData && this.userProfileData.username
      );
      if (!u) return false;
      return (konekoABudgetAdminUsernames || []).some(
        (name) => String(name || "").toLowerCase() === u
      );
    },
    konekoBudgetOrgUsesApi() {
      if (!konekoABudgetBackendEnabled) return false;
      if (this.signedInIsKonekoBudgetAdmin) return true;
      return Boolean(
        this.signedInHasOrgBudgetEditorCap &&
          (this.konekoBudgetBackendUsesApi ||
            !(konekoABudgetBackendAllowlist || []).length)
      );
    },
    myBudgetSubmissionLabel() {
      const sub =
        this.myBudgetSubmission ||
        submissionFromCache(
          this.calendarMonthKey,
          (this.myBudgetEditorUser && this.myBudgetEditorUser.username) || "",
          this.konekoMemberSubmissionsApiCacheByMonth
        );
      return formatKonekoSubmissionLabel(sub);
    },
    teamBudgetSubmissionStats() {
      const groups = this.teamPersonalBudgetGroups;
      const submitted = groups.filter((g) => g.submitted).length;
      return {
        total: groups.length,
        submitted,
        pending: groups.length - submitted,
      };
    },
    teamPersonalBudgetColumnTotals() {
      let minDec = 0;
      let maxDec = 0;
      let usedDec = 0;
      (this.teamPersonalBudgetGroups || []).forEach((group) => {
        (group.projects || []).forEach((row) => {
          minDec += hoursToDecimal(row.low);
          maxDec += hoursToDecimal(row.high);
          usedDec += hoursToDecimal(row.monthLogged);
        });
      });
      return {
        minLabel: formatTeamPersonalBudgetTally(minDec),
        maxLabel: formatTeamPersonalBudgetTally(maxDec),
        usedLabel: formatTeamPersonalBudgetTally(usedDec),
      };
    },
    showKonekoReassignUi() {
      return this.hasKonekoCap(KONEKO_CAP.konekoReassignTimesheet);
    },
    konekoProjectOptions() {
      const list = Array.isArray(this.konekoProjectsList)
        ? this.konekoProjectsList
        : [];
      return list
        .map((p) => ({
          id: p.id,
          name: String(p.project_name || p.name || "").trim(),
        }))
        .filter((p) => p.id && p.name)
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {sensitivity: "base"})
        );
    },
    orgBudgetCatalogNames() {
      const names = new Set();
      (this.konekoProjectOptions || []).forEach((p) => {
        if (p && p.name) names.add(p.name);
      });
      if (!names.size) {
        readKonekoProjectsCatalogFromSession().forEach((p) => {
          if (p && p.name) names.add(p.name);
        });
      }
      return Array.from(names);
    },
    teamPersonalBudgetGroups() {
      if (!this.hasKonekoCap(KONEKO_CAP.konekoTeamPersonalBudgets)) return [];
      const monthKey = this.calendarMonthKey;
      const rosterMeta = this.konekoTeamRosterMetaByUsername || {};
      const apiBucket = this.konekoMemberTargetsApiCacheByMonth[monthKey] || {};
      const members = Array.isArray(this.membersData) ? this.membersData : [];
      const canonKeys = new Map();
      const addKey = (k) => {
        const raw = String(k || "").trim();
        if (!raw) return;
        const canon =
          resolveKonekoAPlanningUsernameKey(raw) ||
          canonicalizeKonekoUsername(raw) ||
          raw;
        if (konekoMemberExcludedForMonth(monthKey, canon, "", rosterMeta)) {
          return;
        }
        if (!canonKeys.has(canon)) canonKeys.set(canon, canon);
      };
      listKonekoAPlanningMemberKeysForBudget(monthKey, rosterMeta).forEach(
        addKey
      );
      Object.keys(apiBucket).forEach(addKey);

      return Array.from(canonKeys.keys())
        .filter(
          (memberKey) =>
            !konekoMemberExcludedForMonth(monthKey, memberKey, "", rosterMeta)
        )
        .map((memberKey) => {
          const memberLabel = formatKonekoAPlanningMemberSelectLabel(memberKey);
          const submission = submissionFromCache(
            monthKey,
            memberKey,
            this.konekoMemberSubmissionsApiCacheByMonth,
            memberLabel
          );
          const submitted = Boolean(submission && submission.count > 0);
          const targets = resolveMemberTargets(
            monthKey,
            memberKey,
            memberLabel,
            this.konekoMemberTargetsApiCacheByMonth
          );
          const memberRow =
            members.find((m) =>
              konekoMemberRowMatchesLookup(
                m.username,
                m.name,
                memberKey,
                memberLabel
              )
            ) || null;
          const loggedProjects = [];
          if (memberRow && Array.isArray(memberRow.allocations)) {
            memberRow.allocations.forEach((line) => {
              if (
                hoursToDecimal(line.monthLogged) > 0 ||
                hoursToDecimal(line.weekLogged) > 0
              ) {
                loggedProjects.push(line.project);
              }
            });
          }
          const projects = resolveCanonicalProjectDisplayNames(
            Object.keys(targets || {}),
            loggedProjects
          )
            .sort((a, b) => a.localeCompare(b))
            .map((project) => {
              const band = normalizeTargetBandForEditor(
                getMemberProjectLimitBand(
                  monthKey,
                  memberKey,
                  project,
                  memberLabel,
                  this.konekoMemberTargetsApiCacheByMonth
                ) || {}
              );
              const allocLine = memberRow
                ? findMemberAllocationLine(memberRow, project)
                : null;
              const monthLoggedDec = allocLine
                ? hoursToDecimal(allocLine.monthLogged)
                : 0;
              const entries =
                allocLine && Array.isArray(allocLine.entries)
                  ? allocLine.entries
                  : [];
              return {
                project,
                low: band.low || "",
                high: band.high || "",
                monthLogged: allocLine ? allocLine.monthLogged : "0:00",
                monthLoggedLabel:
                  monthLoggedDec > 0 ? decimalToHHMM(monthLoggedDec) : "—",
                hasMonthTime: monthLoggedDec > 0,
                entries,
                entryCount: entries.length,
              };
            });
          let minDec = 0;
          let maxDec = 0;
          let usedDec = 0;
          projects.forEach((row) => {
            minDec += hoursToDecimal(row.low);
            maxDec += hoursToDecimal(row.high);
            usedDec += hoursToDecimal(row.monthLogged);
          });
          const savedToNekoHR = memberTargetsSavedInApi(
            monthKey,
            memberKey,
            this.konekoMemberTargetsApiCacheByMonth
          );
          return {
            memberKey,
            memberLabel,
            projects,
            monthUsedLabel: formatTeamPersonalBudgetTally(usedDec),
            savedToNekoHR,
            submitted,
            submission,
            submissionLabel: formatKonekoSubmissionLabel(submission),
            totalsLabel:
              projects.length > 0
                ? `${formatTeamPersonalBudgetTally(
                    minDec
                  )} / ${formatTeamPersonalBudgetTally(maxDec)}`
                : "—",
            minTotalLabel: formatTeamPersonalBudgetTally(minDec),
            maxTotalLabel: formatTeamPersonalBudgetTally(maxDec),
            usedTotalLabel: formatTeamPersonalBudgetTally(usedDec),
            ...(() => {
              const edit = summarizeMemberTimesheetEdits(projects);
              return {
                timesheetEditCount: edit.count,
                timesheetEditChipLabel: edit.chipLabel,
              };
            })(),
          };
        })
        .sort((a, b) => localeNameSort(a.memberLabel, b.memberLabel));
    },
    currentMonthlyBudgetMap() {
      const monthKey = this.calendarMonthKey;
      const base = konekoAMonthlyProjectBudgets[monthKey] || {};
      const api = this.konekoOrgBudgetsApiCacheByMonth[monthKey] || {};
      const draft = this.monthBudgetDraft || {};
      const useApiLayer =
        this.konekoBudgetOrgUsesApi ||
        this.signedInIsKonekoBudgetAdmin ||
        Object.keys(api).length > 0;
      if (useApiLayer) {
        return mergeMonthlyBudgetBandMaps(base, api, draft);
      }
      return mergeMonthlyBudgetBandMaps(base, draft);
    },
    budgetEditorProjectNames() {
      const monthKey = this.calendarMonthKey;
      const rosterMeta = this.konekoTeamRosterMetaByUsername || {};
      const members = (Array.isArray(this.membersData)
        ? this.membersData
        : []
      ).filter(
        (m) =>
          !konekoMemberExcludedForMonth(
            monthKey,
            m.username,
            m.name,
            rosterMeta
          )
      );
      const targetCache = this.konekoMemberTargetsApiCacheByMonth;
      const names = new Set(Object.keys(this.currentMonthlyBudgetMap || {}));

      members.forEach((member) => {
        (member.allocations || []).forEach((line) => {
          if (
            hoursToDecimal(line.monthLogged) > 0 ||
            hoursToDecimal(line.weekLogged) > 0
          ) {
            names.add(line.project);
          }
        });
        const mergedTargets = resolveMemberTargets(
          monthKey,
          member.username,
          member.name,
          targetCache,
          rosterMeta
        );
        Object.keys(mergedTargets || {}).forEach((projectName) => {
          const band = getMemberProjectLimitBand(
            monthKey,
            member.username,
            projectName,
            member.name,
            targetCache,
            rosterMeta
          );
          if (memberDeclaredEstimateDec(band) > 0) {
            names.add(projectName);
          }
        });
      });

      return resolveCanonicalProjectDisplayNames(Array.from(names))
        .filter((projectName) => {
          const band = this.getProjectBudgetBandFromMap(projectName);
          const hasOrg = orgBudgetBandIsConfigured(band);
          const logged = projectMonthLoggedDecimal(members, projectName);
          let hasDeclared = false;
          for (const m of members) {
            const b = getMemberProjectLimitBand(
              monthKey,
              m.username,
              projectName,
              m.name,
              targetCache,
              rosterMeta
            );
            if (memberDeclaredEstimateDec(b) > 0) {
              hasDeclared = true;
              break;
            }
          }
          return hasOrg || logged > 0 || hasDeclared;
        })
        .sort((a, b) => a.localeCompare(b));
    },
    budgetEditorRows() {
      const monthKey = this.calendarMonthKey;
      const mp = this.monthElapsedPercent;
      const rosterMeta = this.konekoTeamRosterMetaByUsername || {};
      const members = (Array.isArray(this.membersData)
        ? this.membersData
        : []
      ).filter(
        (m) =>
          !konekoMemberExcludedForMonth(
            monthKey,
            m.username,
            m.name,
            rosterMeta
          )
      );
      const contributorsByProject = this.monthContributorsByProject || {};
      return this.budgetEditorProjectNames.map((project) => {
        const band =
          this.currentMonthlyBudgetMap[project] ||
          this.getProjectBudgetBandFromMap(project) ||
          {};
        const loggedDec = projectMonthLoggedDecimal(members, project);
        const budgetBar = orgBudgetBandIsConfigured(band)
          ? {current: loggedDec, ...bandToDecimals(band)}
          : null;
        const barStatus = memberBudgetBarStatus(budgetBar, mp);
        const baseline = this.getProjectSnapshotBudgetBaseline(project);
        const snapshotRow = {budgetBar};
        const snapshotBudgetDeltaHours = computeSnapshotBudgetDeltaHours(
          snapshotRow,
          baseline
        );
        const barPos = budgetBarCurrentPositionPct(budgetBar);
        const snapshotPastCalendarPaceLine =
          typeof mp === "number" &&
          Number.isFinite(mp) &&
          mp >= 0 &&
          mp <= 100 &&
          barPos != null &&
          barPos > mp;
        const snapshotOverBudget = Boolean(
          budgetBar && isKonekoBudgetBarOverrun(budgetBar.current, budgetBar)
        );
        return {
          project,
          low: band.low ?? "",
          high: band.high ?? "",
          needsOrgBudget: !orgBudgetBandIsConfigured(band),
          monthLoggedLabel: loggedDec > 0 ? decimalToHHMM(loggedDec) : "—",
          monthLoggedDec: loggedDec,
          budgetBar,
          barStatus: barStatus === "ok" ? "" : barStatus,
          snapshotBudgetDeltaHours,
          snapshotPaceAheadHours: computeSnapshotPaceAheadHours(
            snapshotRow,
            baseline,
            mp
          ),
          snapshotOverBudget,
          snapshotPastCalendarPaceLine,
          contributors: contributorsByProject[project] || [],
        };
      });
    },
    sortedBudgetEditorRows() {
      return sortBudgetEditorRows(
        this.budgetEditorRows,
        this.budgetEditorSortBy
      );
    },
    orgBudgetEditorRowByProject() {
      const map = {};
      (this.budgetEditorRows || []).forEach((row) => {
        if (row && row.project) map[row.project] = row;
      });
      return map;
    },
    budgetEditorColumnTotals() {
      let minDec = 0;
      let maxDec = 0;
      let usedDec = 0;
      (this.budgetEditorRows || []).forEach((row) => {
        minDec += hoursToDecimal(row.low);
        maxDec += hoursToDecimal(row.high);
        usedDec += row.monthLoggedDec || 0;
      });
      return {
        minLabel: formatBudgetEditorTallyWithFte(minDec),
        maxLabel: formatBudgetEditorTallyWithFte(maxDec),
        usedLabel: formatBudgetEditorTallyWithFte(usedDec),
      };
    },
    crossComparisonColumnTotals() {
      let estimateDec = 0;
      let minDec = 0;
      let maxDec = 0;
      let loggedDec = 0;
      (this.crossComparisonRows || []).forEach((row) => {
        estimateDec += hoursToDecimal(row.overallEstimatedHours);
        loggedDec += row.monthLoggedDec || 0;
        if (row.budgetMin !== "—") {
          minDec += hoursToDecimal(row.budgetMin);
        }
        if (row.budgetMax !== "—") {
          maxDec += hoursToDecimal(row.budgetMax);
        }
      });
      return {
        estimateLabel: formatBudgetEditorTallyWithFte(estimateDec),
        minLabel: formatBudgetEditorTallyWithFte(minDec),
        maxLabel: formatBudgetEditorTallyWithFte(maxDec),
        loggedLabel: formatBudgetEditorTallyWithFte(loggedDec),
      };
    },
    historicFteCurrentMonthElapsed() {
      return monthElapsedPercentForKey(dateToMonthKey(new Date()));
    },
    historicFteTableMonths() {
      const currentKey = dateToMonthKey(new Date());
      const formatHoursLabel = (dec) =>
        Math.abs(dec - Math.round(dec)) < 0.01
          ? String(Math.round(dec))
          : dec.toFixed(1);
      const labelWithPrediction = (formatFn, hoursDec, monthKey) => {
        const base = formatFn(hoursDec);
        if (monthKey !== currentKey) return base;
        const elapsed = monthElapsedPercentForKey(monthKey);
        const predicted = predictMonthEndHoursDecimal(hoursDec, elapsed);
        if (predicted == null) return base;
        return `${base} (${formatFn(predicted)})`;
      };
      return listRollingMonthKeys(12, new Date()).map((monthKey) => {
        const hoursDec = this.historicFteByMonth[monthKey];
        const hasData = hoursDec != null && Number.isFinite(hoursDec);
        return {
          monthKey,
          monthLabel: formatHistoricMonthLabel(monthKey),
          fteLabel: hasData
            ? labelWithPrediction(formatHoursDecimalAsFte, hoursDec, monthKey)
            : "—",
          hoursLabel: hasData
            ? labelWithPrediction(formatHoursLabel, hoursDec, monthKey)
            : "—",
          isCurrentMonth: monthKey === currentKey,
        };
      });
    },
    /** Active planning roster for the selected month (excludes departing members). */
    budgetPlanningMemberKeys() {
      return listKonekoAPlanningMemberKeysForBudget(
        this.calendarMonthKey,
        this.konekoTeamRosterMetaByUsername
      );
    },
    monthContributorsByProject() {
      return buildAllMonthContributorsByProject(
        this.membersData,
        this.calendarMonthKey,
        this.konekoMemberTargetsApiCacheByMonth,
        this.monthElapsedPercent,
        this.konekoTeamRosterMetaByUsername
      );
    },
    crossComparisonRows() {
      const monthKey = this.calendarMonthKey;
      const mp = this.monthElapsedPercent;
      const rosterMeta = this.konekoTeamRosterMetaByUsername || {};
      const members = (Array.isArray(this.membersData)
        ? this.membersData
        : []
      ).filter(
        (m) =>
          !konekoMemberExcludedForMonth(
            monthKey,
            m.username,
            m.name,
            rosterMeta
          )
      );
      const targetCache = this.konekoMemberTargetsApiCacheByMonth;
      const rawNames = new Set(Object.keys(this.currentMonthlyBudgetMap || {}));

      members.forEach((member) => {
        (member.allocations || []).forEach((line) => {
          if (
            hoursToDecimal(line.monthLogged) > 0 ||
            hoursToDecimal(line.weekLogged) > 0
          ) {
            rawNames.add(line.project);
          }
        });
      });

      this.budgetPlanningMemberKeys.forEach((rosterKey) => {
        const memberLabel = formatKonekoAPlanningMemberSelectLabel(rosterKey);
        const mergedTargets = resolveMemberTargets(
          monthKey,
          rosterKey,
          memberLabel,
          targetCache,
          rosterMeta
        );
        Object.keys(mergedTargets || {}).forEach((projectName) => {
          const band = getMemberProjectLimitBand(
            monthKey,
            rosterKey,
            projectName,
            memberLabel,
            targetCache,
            rosterMeta
          );
          if (memberDeclaredEstimateDec(band) > 0) {
            rawNames.add(projectName);
          }
        });
      });

      return resolveCanonicalProjectDisplayNames(Array.from(rawNames))
        .map((project) => {
          const canon =
            canonicalizeKonekoProjectName(project) ||
            normalizeProjectLookupKey(project);
          let overallEstimatedHoursDec = 0;
          members.forEach((member) => {
            const mergedTargets = resolveMemberTargets(
              monthKey,
              member.username,
              member.name,
              targetCache
            );
            Object.keys(mergedTargets || {}).forEach((projectName) => {
              const nameCanon =
                canonicalizeKonekoProjectName(projectName) ||
                normalizeProjectLookupKey(projectName);
              if (nameCanon !== canon) return;
              const band = getMemberProjectLimitBand(
                monthKey,
                member.username,
                projectName,
                member.name,
                targetCache
              );
              overallEstimatedHoursDec += memberDeclaredEstimateDec(band);
            });
          });
          const orgBand = this.getProjectBudgetBandFromMap(project);
          const hasOrg = orgBudgetBandIsConfigured(orgBand);
          const minB =
            hasOrg && orgBand.low != null ? hoursToDecimal(orgBand.low) : null;
          const maxB = hasOrg
            ? orgBand.high != null
              ? hoursToDecimal(orgBand.high)
              : orgBand.target != null
              ? hoursToDecimal(orgBand.target)
              : 0
            : 0;
          const overall = overallEstimatedHoursDec;
          const loggedDec = projectMonthLoggedDecimal(members, project);
          let status = "On Track";
          if (!hasOrg) {
            status = "No org budget";
          } else if (maxB > 0 && overall > maxB) {
            status = "OVER BUDGET";
          } else if (minB != null && minB > 0 && overall < minB) {
            status = "UNDER MIN";
          }
          const deltaVsMax = overall - maxB;
          const usagePct = maxB > 0 ? Math.round((overall / maxB) * 100) : null;
          const budgetBar = hasOrg
            ? {current: loggedDec, ...bandToDecimals(orgBand)}
            : null;
          const barStatus = memberBudgetBarStatus(budgetBar, mp);
          const contributors =
            (this.monthContributorsByProject || {})[project] || [];
          return {
            project,
            overallEstimatedHours: decimalToHHMM(overall),
            budgetMin: minB != null ? decimalToHHMM(minB) : "—",
            budgetMax: hasOrg ? decimalToHHMM(maxB) : "—",
            status,
            deltaVsMaxLabel:
              maxB > 0 ? this.formatSignedHours(deltaVsMax) : "—",
            usageLabel: usagePct != null ? `${usagePct}%` : "—",
            budgetBar,
            barStatus: barStatus === "ok" ? "" : barStatus,
            contributors,
            _sortOverall: overall,
            _sortBudgetDelta: maxB > 0 ? deltaVsMax : null,
            monthLoggedDec: loggedDec,
          };
        })
        .filter((row) => {
          const hasOrg = orgBudgetBandIsConfigured(
            this.getProjectBudgetBandFromMap(row.project)
          );
          return hasOrg || row._sortOverall > 0 || row.monthLoggedDec > 0;
        });
    },
    sortedCrossComparisonRows() {
      return sortCrossComparisonRows(
        this.crossComparisonRows,
        this.crossComparisonSortBy
      );
    },
    monthSwitchOptions() {
      const now = new Date();
      const nextDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const keys = new Set([
        ...listRollingMonthKeys(12, now),
        dateToMonthKey(nextDate),
        ...Object.keys(konekoAMonthlyProjectBudgets || {}),
        ...Object.keys(konekoATargetsByMonth || {}),
      ]);
      return Array.from(keys)
        .sort()
        .map((key) => ({text: key, value: key}));
    },
    myBudgetEditorUser() {
      if (this.viewAsPreviewActive && this.globalViewAsPlanningKey) {
        const key = this.globalViewAsPlanningKey;
        return {
          username: key,
          displayName: formatKonekoAPlanningMemberSelectLabel(key) || key,
          userId: this.resolvePlanningKeyToUserId(key),
        };
      }
      return resolveMyBudgetEditorIdentity(this);
    },
    /** True when budget history should use `…/me/budget-history/` (signed-in user editing own caps). */
    myBudgetHistoryForSelf() {
      const user = this.myBudgetEditorUser;
      if (!user || !user.username) return true;
      const self = canonicalizeKonekoUsername(
        this.userProfileData && this.userProfileData.username
      );
      const sub = canonicalizeKonekoUsername(user.username);
      return Boolean(self && sub && self === sub);
    },
    showMyBudgetPanel() {
      if (!this.hasKonekoCap(KONEKO_CAP.konekoMyBudget)) return false;
      const user = this.myBudgetEditorUser;
      if (!user) return false;
      return !konekoMemberExcludedForMonth(
        this.calendarMonthKey,
        user.username,
        user.displayName,
        this.konekoTeamRosterMetaByUsername
      );
    },
    staleKonekoFrontendBundle() {
      return String(options.commitHash || "").includes("__COMMITHASH__");
    },
    myBudgetEditorRows() {
      const user = this.myBudgetEditorUser;
      if (!user) return [];
      const hidden = new Set(this.myMemberTargetsHiddenProjects || []);
      const monthKey = this.calendarMonthKey;
      const fromConfig = resolveMemberTargetsFromConfig(
        monthKey,
        user.username,
        user.displayName
      );
      const draft = this.myMemberTargetsDraft || {};
      const allocProjects = [];
      const me = this.findMyMemberRow();
      if (me && Array.isArray(me.allocations)) {
        me.allocations.forEach((line) => {
          if (line && line.project && !hidden.has(line.project)) {
            allocProjects.push(line.project);
          }
        });
      }
      const projectNames = resolveCanonicalProjectDisplayNames(
        Object.keys(fromConfig),
        Object.keys(draft),
        allocProjects
      ).filter((project) => !hidden.has(project));
      return projectNames
        .sort((a, b) => a.localeCompare(b))
        .map((project) => {
          const band = mergeEditorTargetBandForProject(
            fromConfig,
            draft,
            project
          );
          return {
            project,
            low: band.low ?? "",
            high: band.high ?? "",
          };
        });
    },
    myBudgetEditorProjectNames() {
      return (this.myBudgetEditorRows || []).map((r) => r.project);
    },
    myBudgetEditorColumnTotals() {
      let minDec = 0;
      let maxDec = 0;
      (this.myBudgetEditorRows || []).forEach((row) => {
        minDec += hoursToDecimal(row.low);
        maxDec += hoursToDecimal(row.high);
      });
      return {
        minLabel: formatBudgetEditorTallyWithFte(minDec),
        maxLabel: formatBudgetEditorTallyWithFte(maxDec),
      };
    },
    /** Add-project combobox — full Neko project list (not month timesheet rollup). */
    myBudgetProjectNameSuggestions() {
      const already = new Set(
        (this.myBudgetEditorRows || []).map((r) => r.project)
      );
      const names = new Set();
      (this.konekoProjectOptions || []).forEach((p) => {
        if (p && p.name) names.add(p.name);
      });
      if (!names.size) {
        readKonekoProjectsCatalogFromSession().forEach((p) => {
          if (p.name) names.add(p.name);
        });
      }
      if (!names.size) {
        Object.keys(this.currentMonthlyBudgetMap || {}).forEach((n) => {
          if (n) names.add(n);
        });
      }
      return Array.from(names)
        .filter((name) => name && !already.has(name))
        .sort((a, b) => a.localeCompare(b, undefined, {sensitivity: "base"}));
    },
    weekLabel() {
      return (
        this.formatDate(this.weekStart) + " - " + this.formatDate(this.weekEnd)
      );
    },
    /** YYYY-MM for `konekoAMonthlyProjectBudgets` keys — fill that object for this month in config. */
    calendarMonthKey() {
      return this.selectedMonthKey || currentKonekoCalendarMonthKey();
    },
    /** Prominent month label, e.g. May 2025. */
    calendarMonthDisplayTitle() {
      return formatKonekoMonthDisplayTitle(this.calendarMonthKey);
    },
    isViewingCurrentCalendarMonth() {
      return this.calendarMonthKey === currentKonekoCalendarMonthKey();
    },
    /** Timesheet fetch + logged-hour totals (current month when budget month is still in the future). */
    timesheetMonthKey() {
      return effectiveTimesheetMonthKey(this.calendarMonthKey);
    },
    snapshotUsesTimesheetFallback() {
      return this.timesheetMonthKey !== this.calendarMonthKey;
    },
    isFutureCalendarMonth() {
      return isFutureKonekoCalendarMonthKey(this.calendarMonthKey);
    },
    timesheetMonthStart() {
      const parsed = parseMonthKey(this.timesheetMonthKey);
      if (!parsed) {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
      }
      return new Date(parsed.year, parsed.month - 1, 1);
    },
    timesheetMonthEnd() {
      const parsed = parseMonthKey(this.timesheetMonthKey);
      if (!parsed) {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
      }
      return new Date(parsed.year, parsed.month, 0, 23, 59, 59, 999);
    },
    /** Elapsed share of the timesheet month (pace line), not a future budget-only month. */
    monthElapsedPercent() {
      const now = new Date();
      const start = new Date(this.timesheetMonthStart);
      start.setHours(0, 0, 0, 0);
      const end = new Date(
        this.timesheetMonthStart.getFullYear(),
        this.timesheetMonthStart.getMonth() + 1,
        0,
        23,
        59,
        59,
        999
      );
      const t = Math.min(
        Math.max(now.getTime(), start.getTime()),
        end.getTime()
      );
      const span = end.getTime() - start.getTime();
      if (span <= 0) return 0;
      return Math.min(
        100,
        Math.max(0, Math.round(((t - start.getTime()) / span) * 100))
      );
    },
    weekScopeHeading() {
      return `Week of ${this.weekLabel} (${this.monthElapsedPercent}% through month)`;
    },
    monthLabel() {
      return (
        this.formatDate(this.monthStart) +
        " - " +
        this.formatDate(this.monthEnd)
      );
    },
    isAdminView() {
      return this.$route.query.mode === "admin";
    },
    /** Session-wide admin preview (Dashboard drawer + Koneko A). */
    globalViewAsPlanningKey() {
      if (this.dashboardEmbedFlag) return "";
      return dashboardViewAsState.viewingAsPlanningKeyNonSelf || "";
    },
    viewAsPreviewActive() {
      return Boolean(this.globalViewAsPlanningKey);
    },
    viewAsPreviewLabel() {
      if (!this.globalViewAsPlanningKey) return "";
      return formatKonekoAPlanningMemberSelectLabel(
        this.globalViewAsPlanningKey
      );
    },
    effectiveAdminView() {
      if (this.viewAsPreviewActive) return false;
      // For admin users, default to all-members view unless explicitly forcing self scope.
      return this.isAdminUser && this.$route.query.scope !== "self";
    },
    /**
     * Load all team timesheets (not only signed-in user's).
     * View-as uses the previewed person's caps — team panels need org-wide data.
     */
    loadTimesheetsOrgWide() {
      if (this.dashboardEmbedFlag) return true;
      if (this.effectiveAdminView) return true;
      if (this.signedInIsKonekoAdmin) return true;
      if (
        this.hasKonekoCap(KONEKO_CAP.konekoProjectSnapshot) ||
        this.hasKonekoCap(KONEKO_CAP.konekoCrossComparison) ||
        this.hasKonekoCap(KONEKO_CAP.konekoMemberBreakdownAll) ||
        this.hasKonekoCap(KONEKO_CAP.konekoTeamPersonalBudgets) ||
        this.hasKonekoCap(KONEKO_CAP.konekoOrgBudgetEditor)
      ) {
        return true;
      }
      if (this.viewAsPreviewActive) {
        return (
          this.hasKonekoCap(KONEKO_CAP.konekoProjectSnapshot) ||
          this.hasKonekoCap(KONEKO_CAP.konekoCrossComparison) ||
          this.hasKonekoCap(KONEKO_CAP.konekoMemberBreakdownAll) ||
          this.hasKonekoCap(KONEKO_CAP.konekoTeamPersonalBudgets) ||
          this.hasKonekoCap(KONEKO_CAP.konekoOrgBudgetEditor)
        );
      }
      return false;
    },
    /** When not org-wide, API `user_id` for timesheet fetch (view-as target, not admin). */
    effectiveTimesheetUserId() {
      const members =
        this.konekoWorkloadCache && this.konekoWorkloadCache.members;
      return this.timesheetQueryUserId(members);
    },
    /** When false, Member Breakdown filters to one member (self, route ?member=, or dashboard View as). */
    showAllMembersBreakdown() {
      if (!this.hasKonekoCap(KONEKO_CAP.konekoMemberBreakdownAll)) return false;
      return !this.dashboardEmbedFlag;
    },
    requestedMember() {
      return (this.$route.query.member || "").toLowerCase();
    },
    currentUserKey() {
      return (
        (this.userProfileData && this.userProfileData.username) ||
        ""
      ).toLowerCase();
    },
    /** Signed-in JWT user is a planning admin (not affected by view-as). */
    signedInIsKonekoAdmin() {
      const u = canonicalizeKonekoUsername(
        this.userProfileData && this.userProfileData.username
      );
      if (!u) return false;
      return konekoAAdminUsernames.some((a) => String(a).toLowerCase() === u);
    },
    /** Module constant — not in `data()` (avoids Vue 2 reactivity breaking cap keys). */
    KONEKO_CAP() {
      return KONEKO_CAP;
    },
    canManageKonekoAccess() {
      return isKonekoAccessControlUser(this.userProfileData);
    },
    /** Username whose visibility caps apply (view-as target or self). */
    effectiveKonekoUsername() {
      if (this.viewAsPreviewActive && this.globalViewAsPlanningKey) {
        return this.globalViewAsPlanningKey;
      }
      return (
        canonicalizeKonekoUsername(
          this.userProfileData && this.userProfileData.username
        ) || ""
      );
    },
    konekoCapabilities() {
      void dashboardViewAsState.visibilityOverridesRev;
      return getKonekoCapabilities(this.effectiveKonekoUsername);
    },
    /**
     * UI + scoped data loading for the effective user (view-as uses their caps).
     * @deprecated Prefer hasKonekoCap — kept where admin API writes still need JWT admin.
     */
    isAdminUser() {
      return (
        this.hasKonekoCap(KONEKO_CAP.konekoOrgBudgetEditor) ||
        this.hasKonekoCap(KONEKO_CAP.konekoTeamPersonalBudgets)
      );
    },
    visibleMembers() {
      let raw = this.membersData;
      if (!this.showAllMembersBreakdown) {
        const embedRaw = String(
          this.dashboardEmbedFlag ? this.embeddedViewAsUsername || "" : ""
        ).trim();
        const selfCanon = canonicalizeKonekoUsername(
          this.userProfileData && this.userProfileData.username
        );
        const embedCanon = embedRaw ? canonicalizeKonekoUsername(embedRaw) : "";
        const dashboardViewAsKey =
          embedRaw && embedCanon && (!selfCanon || embedCanon !== selfCanon)
            ? embedRaw
            : "";
        const planningKeyOnly = String(
          this.dashboardEmbedFlag
            ? dashboardViewAsKey
            : this.requestedMember || this.globalViewAsPlanningKey || ""
        ).trim();

        let lookupUsername = "";
        let lookupDisplayName = "";
        let found;

        if (planningKeyOnly) {
          lookupUsername = planningKeyOnly;
          lookupDisplayName = "";
          found = this.membersData.find((m) =>
            konekoPlanningKeyMatchesMemberRow(m.username, planningKeyOnly)
          );
          if (!found) {
            found = this.membersData.find((m) =>
              konekoMemberRowMatchesLookup(
                m.username,
                m.name,
                planningKeyOnly,
                ""
              )
            );
          }
          if (!found) {
            const pretty = formatKonekoAPlanningMemberSelectLabel(
              planningKeyOnly
            );
            found = this.membersData.find((m) =>
              konekoMemberRowMatchesLookup(
                m.username,
                m.name,
                planningKeyOnly,
                pretty
              )
            );
          }
        } else if (this.dashboardEmbedFlag) {
          lookupUsername =
            (this.userProfileData && this.userProfileData.username) || "";
          lookupDisplayName =
            (this.userProfileData && this.userProfileData.full_name) || "";
          const hasLookup = Boolean(
            String(lookupUsername || "").trim() ||
              String(lookupDisplayName || "").trim()
          );
          found =
            hasLookup &&
            this.membersData.find((m) =>
              konekoMemberRowMatchesLookup(
                m.username,
                m.name,
                lookupUsername,
                lookupDisplayName
              )
            );
        } else {
          lookupUsername =
            (this.userProfileData && this.userProfileData.username) || "";
          lookupDisplayName =
            (this.userProfileData && this.userProfileData.full_name) || "";
          const hasLookup = Boolean(
            String(lookupUsername || "").trim() ||
              String(lookupDisplayName || "").trim()
          );
          found =
            hasLookup &&
            this.membersData.find((m) =>
              konekoMemberRowMatchesLookup(
                m.username,
                m.name,
                lookupUsername,
                lookupDisplayName
              )
            );
        }
        raw = found ? [found] : [];
        if (!raw.length && !this.showAllMembersBreakdown) {
          const u = resolveMyBudgetEditorIdentity(this);
          if (u && (u.username || u.displayName)) {
            const key =
              resolveKonekoAPlanningUsernameKey(u.username) ||
              canonicalizeKonekoUsername(u.username) ||
              String(u.username || "").trim();
            if (key) {
              raw = [
                {
                  username: key,
                  name: u.displayName || key,
                  targetHours: "0:00",
                  loggedHours: "0:00",
                  lastLogged: "—",
                  daysSinceDailyReport: "N/A",
                  allocations: [],
                },
              ];
            }
          }
        }
      }
      const monthKey = this.calendarMonthKey;
      const pruned = pruneMembersForDisplay(
        raw,
        monthKey,
        this.konekoTeamRosterMetaByUsername
      );
      const targetCache = this.konekoMemberTargetsApiCacheByMonth;
      const mapped = pruned.map((m) => {
        const allocations = m.allocations.map((line) => {
          const band = getMemberProjectLimitBand(
            monthKey,
            m.username,
            line.project,
            m.name,
            targetCache
          );
          const dec = band
            ? bandToDecimals(band)
            : {low: null, high: null, target: null};
          const target =
            band && (band.target || band.high || band.low)
              ? String(band.target || band.high || band.low)
              : null;
          let percent = null;
          if (target) {
            const targetDec = hoursToDecimal(target);
            const monthDec = hoursToDecimal(line.monthLogged);
            if (targetDec > 0) {
              percent = `${Math.round((monthDec / targetDec) * 100)}%`;
            }
          }
          const fallbackTargetDecimal =
            target && hoursToDecimal(target) > 0
              ? hoursToDecimal(target)
              : null;
          const personalBudgetBar = {
            current: hoursToDecimal(line.monthLogged),
            low: dec.low,
            high: dec.high,
            target: dec.target != null ? dec.target : fallbackTargetDecimal,
          };
          const barStatus = memberBudgetBarStatus(
            personalBudgetBar,
            this.monthElapsedPercent
          );
          return {
            ...line,
            target,
            percent,
            personalBudgetBar,
            barStatus: barStatus === "ok" ? "" : barStatus,
          };
        });
        const targetSumDec = allocations.reduce(
          (sum, a) => sum + (a.target ? hoursToDecimal(a.target) : 0),
          0
        );
        const targetMinSumDec = allocations.reduce(
          (sum, a) =>
            sum +
            (a.personalBudgetBar && a.personalBudgetBar.low != null
              ? a.personalBudgetBar.low
              : a.target
              ? hoursToDecimal(a.target)
              : 0),
          0
        );
        const targetMaxSumDec = allocations.reduce(
          (sum, a) =>
            sum +
            (a.personalBudgetBar && a.personalBudgetBar.high != null
              ? a.personalBudgetBar.high
              : a.target
              ? hoursToDecimal(a.target)
              : 0),
          0
        );
        return {
          ...m,
          allocations,
          targetMinHours: decimalToHHMM(targetMinSumDec),
          targetMaxHours: decimalToHHMM(targetMaxSumDec),
          targetHours: decimalToHHMM(targetMaxSumDec || targetSumDec),
        };
      });
      return sortVisibleMembers(
        mapped,
        this.memberBreakdownSortBy,
        hoursToDecimal
      );
    },
    buildStamp() {
      return `${options.buildType}${options.commitHash}`;
    },
    versionStamp() {
      return options.version || "0.0.0";
    },
    monthStart() {
      const parsed = parseMonthKey(this.calendarMonthKey);
      if (!parsed) {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth(), 1);
      }
      return new Date(parsed.year, parsed.month - 1, 1);
    },
    monthEnd() {
      const parsed = parseMonthKey(this.calendarMonthKey);
      if (!parsed) {
        const d = new Date();
        return new Date(d.getFullYear(), d.getMonth() + 1, 0);
      }
      return new Date(parsed.year, parsed.month, 0);
    },
    weekStart() {
      const d = new Date();
      const day = d.getDay();
      const diffToMonday = day === 0 ? -6 : 1 - day;
      const start = new Date(d);
      start.setDate(d.getDate() + diffToMonday);
      start.setHours(0, 0, 0, 0);
      return start;
    },
    weekEnd() {
      const end = new Date(this.weekStart);
      end.setDate(end.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      return end;
    },
  },
  methods: {
    persistMemberBreakdownSortBy() {
      writeKonekoSortPref(
        STORAGE_MEMBER_BREAKDOWN_SORT,
        this.memberBreakdownSortBy
      );
    },
    persistCrossComparisonSortBy() {
      writeKonekoSortPref(
        STORAGE_CROSS_COMPARISON_SORT,
        this.crossComparisonSortBy
      );
    },
    persistBudgetEditorSortBy() {
      writeKonekoSortPref(STORAGE_BUDGET_EDITOR_SORT, this.budgetEditorSortBy);
    },
    budgetStatusTextClass(status) {
      return konekoBudgetStatusTextClass(status);
    },
    /**
     * Worst per-project status across a member's allocations so the outer
     * (collapsed) member name flags it: red if any project is over cap, else
     * amber if any is ahead of pace, else neutral. Healthy "good" rows are not
     * propagated to the name.
     */
    memberBreakdownWorstStatus(member) {
      const lines = (member && member.allocations) || [];
      let hasPace = false;
      for (const line of lines) {
        if (line.barStatus === "over") return "over";
        if (line.barStatus === "pace") hasPace = true;
      }
      return hasPace ? "pace" : "";
    },
    memberBreakdownWorstStatusClass(member) {
      return konekoBudgetStatusTextClass(
        this.memberBreakdownWorstStatus(member)
      );
    },
    isTeamBudgetEditing(memberKey, project, field) {
      const e = this.teamBudgetEdit;
      return Boolean(
        e &&
          e.memberKey === memberKey &&
          e.project === project &&
          e.field === field
      );
    },
    startTeamBudgetEdit(memberKey, project, field, currentValue) {
      this.teamBudgetEdit = {memberKey, project, field};
      this.teamBudgetEditValue = String(currentValue || "");
    },
    cancelTeamBudgetEdit() {
      this.teamBudgetEdit = null;
      this.teamBudgetEditValue = "";
    },
    async commitTeamBudgetEdit() {
      const e = this.teamBudgetEdit;
      if (!e || !this.konekoBudgetOrgUsesApi) {
        this.cancelTeamBudgetEdit();
        return;
      }
      const monthKey = this.calendarMonthKey;
      if (
        konekoMemberExcludedForMonth(
          monthKey,
          e.memberKey,
          "",
          this.konekoTeamRosterMetaByUsername
        )
      ) {
        this.konekoBudgetApiError =
          "This person no longer has a personal budget for this month.";
        this.cancelTeamBudgetEdit();
        return;
      }
      const memberLabel = formatKonekoAPlanningMemberSelectLabel(e.memberKey);
      const targets = resolveMemberTargets(
        monthKey,
        e.memberKey,
        memberLabel,
        this.konekoMemberTargetsApiCacheByMonth
      );
      const prev = normalizeTargetBandForEditor(targets[e.project] || {});
      const next = {
        ...prev,
        [e.field]: normalizeBudgetHhmmInput(this.teamBudgetEditValue),
      };
      targets[e.project] = next;
      this.teamBudgetSaving = true;
      this.konekoBudgetApiError = "";
      try {
        const res = await saveRosterMemberMonthTargets(
          monthKey,
          e.memberKey,
          targets,
          true
        );
        const canon = canonicalizeKonekoUsername(e.memberKey);
        if (res && res.targets) {
          const bucket =
            this.konekoMemberTargetsApiCacheByMonth[monthKey] || {};
          this.$set(this.konekoMemberTargetsApiCacheByMonth, monthKey, {
            ...bucket,
            [canon]: res.targets,
            [e.memberKey]: res.targets,
          });
        }
        if (res && res.submission) {
          const subBucket =
            this.konekoMemberSubmissionsApiCacheByMonth[monthKey] || {};
          this.$set(this.konekoMemberSubmissionsApiCacheByMonth, monthKey, {
            ...subBucket,
            [canon]: res.submission,
          });
        }
        this.cancelTeamBudgetEdit();
        this.rebuildWorkloadFromCache();
      } catch (err) {
        this.konekoBudgetApiError = String(
          konekoBudgetApiErrorMessage(err) || "Failed to save team budget"
        );
      } finally {
        this.teamBudgetSaving = false;
      }
    },
    memberBreakdownProjectKey(username, project) {
      return `${username}::${project}`;
    },
    isMemberBreakdownProjectExpanded(username, project) {
      return (
        this.memberBreakdownProjectExpand ===
        this.memberBreakdownProjectKey(username, project)
      );
    },
    toggleMemberBreakdownProject(username, project) {
      const key = this.memberBreakdownProjectKey(username, project);
      this.memberBreakdownProjectExpand =
        this.memberBreakdownProjectExpand === key ? null : key;
    },
    teamBudgetProjectKey(memberKey, project) {
      return `${memberKey}::${project}`;
    },
    isTeamBudgetProjectExpanded(memberKey, project) {
      return (this.teamBudgetProjectExpandKeys || []).includes(
        this.teamBudgetProjectKey(memberKey, project)
      );
    },
    toggleTeamBudgetProject(memberKey, project) {
      const key = this.teamBudgetProjectKey(memberKey, project);
      const set = new Set(this.teamBudgetProjectExpandKeys || []);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      this.teamBudgetProjectExpandKeys = Array.from(set);
    },
    monthBudgetStorageKey(monthKey = this.calendarMonthKey) {
      return `konekoA:budgetDraft:${monthKey}`;
    },
    async prefetchKonekoBudgetApiForMonth(monthKey = this.calendarMonthKey) {
      if (!konekoABudgetBackendEnabled) return;
      const user = this.myBudgetEditorUser;
      const mayUseApi =
        this.isAdminUser ||
        (user && isKonekoBudgetBackendEnabledForUser(user.username));
      if (!mayUseApi) return;
      this.konekoBudgetApiLoading = true;
      this.konekoBudgetApiError = "";
      try {
        if (this.signedInIsKonekoAdmin) {
          const roster = await fetchRosterMonthTargets(monthKey);
          if (roster && roster.targetsByUser) {
            this.$set(
              this.konekoMemberTargetsApiCacheByMonth,
              monthKey,
              roster.targetsByUser
            );
          }
          if (roster && roster.submissionsByUser) {
            this.$set(
              this.konekoMemberSubmissionsApiCacheByMonth,
              monthKey,
              roster.submissionsByUser
            );
          }
        }
        if (this.signedInIsKonekoBudgetAdmin) {
          const org = await fetchOrgMonthBudgets(monthKey);
          if (org && org.budgets) {
            this.$set(
              this.konekoOrgBudgetsApiCacheByMonth,
              monthKey,
              org.budgets
            );
          }
        }
        if (!this.signedInIsKonekoAdmin) {
          const me = await fetchMyMonthTargets(monthKey);
          if (me && me.targets) {
            const canon = canonicalizeKonekoUsername(user.username);
            const bucket =
              this.konekoMemberTargetsApiCacheByMonth[monthKey] || {};
            this.$set(this.konekoMemberTargetsApiCacheByMonth, monthKey, {
              ...bucket,
              [canon]: me.targets,
            });
          }
          if (me && me.submission) {
            this.myBudgetSubmission = me.submission;
            const canon = canonicalizeKonekoUsername(user.username);
            const subBucket =
              this.konekoMemberSubmissionsApiCacheByMonth[monthKey] || {};
            this.$set(this.konekoMemberSubmissionsApiCacheByMonth, monthKey, {
              ...subBucket,
              [canon]: me.submission,
            });
          }
        }
      } catch (e) {
        this.konekoBudgetApiError = String(konekoBudgetApiErrorMessage(e));
      } finally {
        this.konekoBudgetApiLoading = false;
      }
    },
    async loadMonthBudgetDraft() {
      const monthKey = this.calendarMonthKey;
      this.monthBudgetDirtyProjects = {};
      this.konekoBudgetSaveOk = "";
      if (this.konekoBudgetOrgUsesApi) {
        await this.prefetchKonekoBudgetApiForMonth(monthKey);
        const api = this.konekoOrgBudgetsApiCacheByMonth[monthKey] || {};
        // Draft = persisted API rows + your edits only (not static config baseline).
        this.monthBudgetDraft = this.normalizeOrgBudgetDraft({...api});
        return;
      }
      let draft = {};
      try {
        const raw = localStorage.getItem(this.monthBudgetStorageKey());
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed && typeof parsed === "object") draft = parsed;
      } catch {
        draft = {};
      }
      this.monthBudgetDraft = this.normalizeOrgBudgetDraft({...draft});
    },
    normalizeOrgBudgetDraft(draft) {
      return normalizeBudgetProjectDraftMap(
        draft,
        this.orgBudgetCatalogNames()
      );
    },
    buildOrgBudgetSavePayload() {
      const rowByProject = this.orgBudgetEditorRowByProject;
      const payload = {};
      const draft = this.monthBudgetDraft || {};
      const dirty = this.monthBudgetDirtyProjects || {};
      const addRowBand = (project) => {
        const row = rowByProject[project];
        if (!row) return;
        payload[project] = {
          low: String(row.low || "").trim(),
          high: String(row.high || "").trim(),
        };
      };
      Object.keys(dirty).forEach((project) => {
        if (dirty[project]) addRowBand(project);
      });
      Object.keys(draft).forEach((project) => {
        if (payload[project]) return;
        if (rowByProject[project]) addRowBand(project);
        else payload[project] = normalizeTargetBandForEditor(draft[project]);
      });
      return this.normalizeOrgBudgetDraft(payload);
    },
    buildOrgBudgetSavePayloadForProject(project) {
      const row = this.orgBudgetEditorRowByProject[project];
      if (row) {
        return this.normalizeOrgBudgetDraft({
          [project]: {
            low: String(row.low || "").trim(),
            high: String(row.high || "").trim(),
          },
        });
      }
      const draft = (this.monthBudgetDraft || {})[project];
      if (!draft) return {};
      return this.normalizeOrgBudgetDraft({[project]: draft});
    },
    applyOrgBudgetApiResponse(monthKey, budgets) {
      if (!budgets || typeof budgets !== "object") return;
      const normalized = this.normalizeOrgBudgetDraft(budgets);
      const prevApi = this.konekoOrgBudgetsApiCacheByMonth[monthKey] || {};
      this.$set(this.konekoOrgBudgetsApiCacheByMonth, monthKey, {
        ...prevApi,
        ...normalized,
      });
      this.monthBudgetDraft = mergeMonthlyBudgetBandMaps(
        this.monthBudgetDraft || {},
        normalized
      );
    },
    async saveOrgBudgetProjectBand(project) {
      const monthKey = this.calendarMonthKey;
      const payload = this.buildOrgBudgetSavePayloadForProject(project);
      const saveKey = Object.keys(payload)[0];
      if (!saveKey) return false;
      if (!this.konekoBudgetOrgUsesApi) {
        try {
          const merged = mergeMonthlyBudgetBandMaps(
            JSON.parse(
              localStorage.getItem(this.monthBudgetStorageKey()) || "{}"
            ),
            payload
          );
          localStorage.setItem(
            this.monthBudgetStorageKey(),
            JSON.stringify(this.normalizeOrgBudgetDraft(merged))
          );
          this.monthBudgetDraft = this.normalizeOrgBudgetDraft(merged);
          this.$set(this.monthBudgetDirtyProjects, saveKey, false);
          return true;
        } catch {
          return false;
        }
      }
      this.konekoBudgetApiError = "";
      try {
        const res = await saveOrgMonthBudgets(monthKey, payload, false);
        if (res && res.budgets) {
          this.applyOrgBudgetApiResponse(monthKey, res.budgets);
        } else {
          this.applyOrgBudgetApiResponse(monthKey, payload);
        }
        this.$set(this.monthBudgetDirtyProjects, saveKey, false);
        this.konekoBudgetSaveOk = `Saved ${saveKey} (${monthKey}).`;
        persistKonekoSelectedMonthKey(monthKey);
        if (String(this.$route.query.month || "").trim() !== monthKey) {
          this.onMonthChange(monthKey);
        }
        this.rebuildWorkloadFromCache();
        return true;
      } catch (e) {
        this.konekoBudgetSaveOk = "";
        this.konekoBudgetApiError = String(
          konekoBudgetApiErrorMessage(e) || "Failed to save org budget"
        );
        return false;
      }
    },
    async commitOrgBudgetField(project, field, value) {
      this.updateBudgetDraft(project, field, value);
      if (this.orgBudgetAutoSaveTimer) {
        clearTimeout(this.orgBudgetAutoSaveTimer);
        this.orgBudgetAutoSaveTimer = null;
      }
      const rowByProject = this.orgBudgetEditorRowByProject;
      const saveKey =
        Object.keys(this.buildOrgBudgetSavePayloadForProject(project))[0] ||
        project;
      this.orgBudgetAutoSaveTimer = setTimeout(async () => {
        this.orgBudgetAutoSaveTimer = null;
        const row = rowByProject[saveKey] || rowByProject[project];
        if (!row) return;
        this.monthBudgetSaving = true;
        try {
          await this.saveOrgBudgetProjectBand(saveKey);
        } finally {
          this.monthBudgetSaving = false;
        }
      }, 600);
    },
    beforeDestroy() {
      if (this.orgBudgetAutoSaveTimer) {
        clearTimeout(this.orgBudgetAutoSaveTimer);
        this.orgBudgetAutoSaveTimer = null;
      }
    },
    async saveMonthBudgetDraft(options = {}) {
      const replace = Boolean(options.replace);
      const monthKey = this.calendarMonthKey;
      const payload = replace
        ? this.normalizeOrgBudgetDraft(
            options.payload || this.monthBudgetDraft || {}
          )
        : this.buildOrgBudgetSavePayload();
      if (!Object.keys(payload || {}).length) {
        this.konekoBudgetSaveOk = "";
        this.konekoBudgetApiError =
          "Nothing to save — edit a Min or Max field in Budget Editor, then click Save.";
        return;
      }
      if (this.konekoBudgetOrgUsesApi) {
        this.monthBudgetSaving = true;
        this.konekoBudgetApiError = "";
        this.konekoBudgetSaveOk = "";
        try {
          const res = await saveOrgMonthBudgets(monthKey, payload, replace);
          if (res && res.budgets) {
            this.applyOrgBudgetApiResponse(monthKey, res.budgets);
          } else if (!replace) {
            this.applyOrgBudgetApiResponse(monthKey, payload);
          }
          this.monthBudgetDirtyProjects = {};
          this.konekoBudgetSaveOk = `Saved org budgets for ${monthKey}.`;
          persistKonekoSelectedMonthKey(monthKey);
          if (String(this.$route.query.month || "").trim() !== monthKey) {
            this.onMonthChange(monthKey);
          }
          try {
            localStorage.removeItem(this.monthBudgetStorageKey());
          } catch {
            // ignore
          }
          this.rebuildWorkloadFromCache();
        } catch (e) {
          this.konekoBudgetSaveOk = "";
          this.konekoBudgetApiError = String(
            konekoBudgetApiErrorMessage(e) || "Failed to save org budgets"
          );
        } finally {
          this.monthBudgetSaving = false;
        }
        return;
      }
      try {
        localStorage.setItem(
          this.monthBudgetStorageKey(),
          JSON.stringify(payload)
        );
        this.monthBudgetDraft = payload;
        this.monthBudgetDirtyProjects = {};
        this.konekoBudgetSaveOk = `Saved org budgets locally for ${monthKey}.`;
        this.konekoBudgetApiError = "";
      } catch {
        this.konekoBudgetSaveOk = "";
        this.konekoBudgetApiError =
          "Could not save org budget in this browser (storage unavailable).";
      }
    },
    async resetMonthBudgetDraft() {
      const monthKey = this.calendarMonthKey;
      const base = konekoAMonthlyProjectBudgets[monthKey] || {};
      const payload = this.normalizeOrgBudgetDraft({...base});
      this.monthBudgetDraft = payload;
      Object.keys(payload).forEach((project) => {
        this.$set(this.monthBudgetDirtyProjects, project, true);
      });
      await this.saveMonthBudgetDraft({replace: true, payload});
    },
    markOrgBudgetProjectDirty(project) {
      const catalog = this.orgBudgetCatalogNames();
      const canon = projectCanonKey(project);
      let saveKey =
        resolveCanonicalProjectDisplayNames([project])[0] ||
        String(project || "").trim();
      const catalogMatch = catalog.find((n) => projectCanonKey(n) === canon);
      if (catalogMatch) saveKey = catalogMatch;
      this.$set(this.monthBudgetDirtyProjects, saveKey, true);
      this.konekoBudgetSaveOk = "";
    },
    updateBudgetDraft(project, field, value) {
      const catalog = this.orgBudgetCatalogNames();
      const canon = projectCanonKey(project);
      let saveKey =
        resolveCanonicalProjectDisplayNames([project])[0] ||
        String(project || "").trim();
      const catalogMatch = catalog.find((n) => projectCanonKey(n) === canon);
      if (catalogMatch) saveKey = catalogMatch;
      const draft = {...(this.monthBudgetDraft || {})};
      Object.keys(draft).forEach((key) => {
        if (key !== saveKey && projectCanonKey(key) === canon) {
          delete draft[key];
        }
      });
      const displayed = normalizeTargetBandForEditor(
        this.currentMonthlyBudgetMap[saveKey] ||
          this.currentMonthlyBudgetMap[project] ||
          {}
      );
      const prev = {
        ...displayed,
        ...normalizeTargetBandForEditor(draft[saveKey]),
      };
      draft[saveKey] = {
        ...prev,
        [field]: String(value || "").trim(),
      };
      this.monthBudgetDraft = draft;
      this.markOrgBudgetProjectDirty(saveKey);
    },
    /**
     * Commit a budget-editor field on Enter/blur only (not per keystroke) so the
     * rows/bars/sort don't recompute on every typed character.
     */
    commitBudgetDraftField(project, field, value) {
      this.updateBudgetDraft(project, field, value);
      this.normalizeBudgetDraftField(project, field);
    },
    normalizeBudgetDraftField(project, field, draftKey = "monthBudgetDraft") {
      const draft = this[draftKey] || {};
      const prev = draft[project] || {};
      const normalized = normalizeBudgetHhmmInput(prev[field]);
      if (normalized === prev[field]) return;
      this.$set(draft, project, {...prev, [field]: normalized});
    },
    findMyMemberRow() {
      const user = this.myBudgetEditorUser;
      if (!user) return null;
      const rows = Array.isArray(this.membersData) ? this.membersData : [];
      return (
        rows.find((m) =>
          konekoMemberRowMatchesLookup(
            m.username,
            m.name,
            user.username,
            user.displayName
          )
        ) || null
      );
    },
    myMemberTargetsDraftForSave() {
      const draft = {...(this.myMemberTargetsDraft || {})};
      const hidden = new Set(this.myMemberTargetsHiddenProjects || []);
      hidden.forEach((project) => {
        delete draft[project];
      });
      return draft;
    },
    persistMyMemberTargetsHiddenProjects() {
      const user = this.myBudgetEditorUser;
      if (!user) return;
      writeMemberTargetsHiddenProjects(
        this.calendarMonthKey,
        user.username,
        user.displayName,
        this.myMemberTargetsHiddenProjects || []
      );
    },
    async loadMyMemberTargetsDraft() {
      const user = this.myBudgetEditorUser;
      if (!user) {
        this.myMemberTargetsDraft = {};
        this.myMemberTargetsHiddenProjects = [];
        return;
      }
      const monthKey = this.calendarMonthKey;
      this.myMemberTargetsHiddenProjects = readMemberTargetsHiddenProjects(
        monthKey,
        user.username,
        user.displayName
      );
      const hidden = new Set(this.myMemberTargetsHiddenProjects);
      if (this.konekoBudgetBackendUsesApi) {
        await this.prefetchKonekoBudgetApiForMonth(monthKey);
      }
      let merged;
      if (this.konekoBudgetBackendUsesApi) {
        merged = resolveMemberTargets(
          monthKey,
          user.username,
          user.displayName,
          this.konekoMemberTargetsApiCacheByMonth
        );
      } else {
        const fromConfig = resolveMemberTargetsFromConfig(
          monthKey,
          user.username,
          user.displayName
        );
        merged = {...fromConfig};
        const stored = readMemberTargetsLocalDraft(
          monthKey,
          user.username,
          user.displayName
        );
        Object.keys(stored).forEach((project) => {
          merged[project] = {
            ...normalizeTargetBandForEditor(merged[project]),
            ...normalizeTargetBandForEditor(stored[project]),
          };
        });
      }
      const me = this.findMyMemberRow();
      if (me && Array.isArray(me.allocations)) {
        me.allocations.forEach((line) => {
          if (!line || !line.project || hidden.has(line.project)) return;
          if (!merged[line.project]) {
            merged[line.project] = {low: "", high: ""};
          }
        });
      }
      hidden.forEach((project) => {
        delete merged[project];
      });
      this.myMemberTargetsDraft = this.normalizeOrgBudgetDraft(merged);
    },
    async saveMyMemberTargetsDraft() {
      const user = this.myBudgetEditorUser;
      if (!user) return;
      const monthKey = this.calendarMonthKey;
      if (
        konekoMemberExcludedForMonth(
          monthKey,
          user.username,
          user.displayName,
          this.konekoTeamRosterMetaByUsername
        )
      ) {
        this.konekoBudgetApiError =
          "Personal budgets are closed for your account from this month.";
        return;
      }
      if (this.konekoBudgetBackendUsesApi) {
        try {
          const payload = this.normalizeOrgBudgetDraft(
            this.myMemberTargetsDraftForSave()
          );
          const res = await saveMyMonthTargets(monthKey, payload, true);
          if (res && res.targets) {
            const bucket =
              this.konekoMemberTargetsApiCacheByMonth[monthKey] || {};
            const canon = canonicalizeKonekoUsername(user.username);
            this.$set(this.konekoMemberTargetsApiCacheByMonth, monthKey, {
              ...bucket,
              [canon]: res.targets,
            });
          }
          if (res && res.submission) {
            this.myBudgetSubmission = res.submission;
            const canon = canonicalizeKonekoUsername(user.username);
            const subBucket =
              this.konekoMemberSubmissionsApiCacheByMonth[monthKey] || {};
            this.$set(this.konekoMemberSubmissionsApiCacheByMonth, monthKey, {
              ...subBucket,
              [canon]: res.submission,
            });
          }
          clearMemberTargetsLocalDraft(
            monthKey,
            user.username,
            user.displayName
          );
          this.persistMyMemberTargetsHiddenProjects();
          this.konekoBudgetApiError = "";
          this.rebuildWorkloadFromCache();
          this.refreshKonekoBudgetHistoryPanel();
        } catch (e) {
          this.konekoBudgetApiError = String(
            konekoBudgetApiErrorMessage(e) || "Failed to save personal budget"
          );
        }
        return;
      }
      writeMemberTargetsLocalDraft(
        monthKey,
        user.username,
        user.displayName,
        this.myMemberTargetsDraftForSave()
      );
      this.persistMyMemberTargetsHiddenProjects();
      this.rebuildWorkloadFromCache();
    },
    async resetMyMemberTargetsDraft() {
      const user = this.myBudgetEditorUser;
      if (!user) return;
      const monthKey = this.calendarMonthKey;
      if (this.konekoBudgetBackendUsesApi) {
        try {
          await saveMyMonthTargets(monthKey, {}, true);
          const bucket =
            this.konekoMemberTargetsApiCacheByMonth[monthKey] || {};
          const canon = canonicalizeKonekoUsername(user.username);
          const next = {...bucket};
          delete next[canon];
          this.$set(this.konekoMemberTargetsApiCacheByMonth, monthKey, next);
          clearMemberTargetsLocalDraft(
            monthKey,
            user.username,
            user.displayName
          );
          clearMemberTargetsHiddenProjects(
            monthKey,
            user.username,
            user.displayName
          );
          this.myMemberTargetsHiddenProjects = [];
          this.konekoBudgetApiError = "";
        } catch (e) {
          this.konekoBudgetApiError = String(
            konekoBudgetApiErrorMessage(e) || "Failed to reset personal budget"
          );
        }
      } else {
        clearMemberTargetsLocalDraft(monthKey, user.username, user.displayName);
        clearMemberTargetsHiddenProjects(
          monthKey,
          user.username,
          user.displayName
        );
        this.myMemberTargetsHiddenProjects = [];
      }
      await this.loadMyMemberTargetsDraft();
      this.rebuildWorkloadFromCache();
      this.refreshKonekoBudgetHistoryPanel();
    },
    updateMyMemberTargetsDraft(project, field, value) {
      const prev = this.myMemberTargetsDraft[project] || {};
      const next = {
        ...prev,
        [field]: String(value || "").trim(),
      };
      this.$set(this.myMemberTargetsDraft, project, next);
    },
    normalizeMyMemberTargetsDraftField(project, field) {
      this.normalizeBudgetDraftField(project, field, "myMemberTargetsDraft");
    },
    addMyBudgetEditorProject(projectName) {
      const name = String(projectName || "").trim();
      if (!name) return;
      this.myMemberTargetsHiddenProjects = (
        this.myMemberTargetsHiddenProjects || []
      ).filter((p) => p !== name);
      this.persistMyMemberTargetsHiddenProjects();
      const prev = this.myMemberTargetsDraft[name] || {low: "", high: ""};
      this.$set(this.myMemberTargetsDraft, name, {...prev});
    },
    removeMyBudgetEditorProject(projectName) {
      const name = String(projectName || "").trim();
      if (!name) return;
      const hidden = [...(this.myMemberTargetsHiddenProjects || [])];
      if (!hidden.includes(name)) hidden.push(name);
      this.myMemberTargetsHiddenProjects = hidden;
      this.persistMyMemberTargetsHiddenProjects();
      if (this.myMemberTargetsDraft[name]) {
        this.$delete(this.myMemberTargetsDraft, name);
      }
    },
    sumOrgMonthLoggedHours() {
      return (this.membersData || []).reduce(
        (sum, m) => sum + hoursToDecimal(m.loggedHours),
        0
      );
    },
    syncHistoricFteFromLoadedMonth() {
      if (
        !this.hasKonekoCap(KONEKO_CAP.konekoOrgBudgetEditor) ||
        !this.calendarMonthKey
      ) {
        return;
      }
      const hoursDec = this.sumOrgMonthLoggedHours();
      this.$set(this.historicFteByMonth, this.calendarMonthKey, hoursDec);
    },
    historicFteCacheStorageKey(monthKey) {
      return `konekoA:historicFteHours:${monthKey}`;
    },
    readHistoricFteHoursCache(monthKey) {
      const currentKey = dateToMonthKey(new Date());
      if (monthKey === currentKey) return null;
      try {
        const raw = localStorage.getItem(
          this.historicFteCacheStorageKey(monthKey)
        );
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const hoursDec = parsed && parsed.hoursDec;
        return Number.isFinite(hoursDec) ? hoursDec : null;
      } catch {
        return null;
      }
    },
    writeHistoricFteHoursCache(monthKey, hoursDec) {
      const currentKey = dateToMonthKey(new Date());
      if (monthKey === currentKey) return;
      try {
        localStorage.setItem(
          this.historicFteCacheStorageKey(monthKey),
          JSON.stringify({hoursDec, savedAt: new Date().toISOString()})
        );
      } catch {
        // ignore quota errors
      }
    },
    formatApiDateForTimesheetQuery(d) {
      return this.formatDate(d).replaceAll(".", "-");
    },
    sumTimesheetHoursInMonthRange(timesheets, monthKey) {
      const range = monthDateRangeFromKey(monthKey);
      if (!range) return 0;
      const rows = Array.isArray(timesheets) ? timesheets : [];
      let total = 0;
      rows.forEach((row) => {
        const rowDate = this.dateFromRow(row);
        if (!rowDate || Number.isNaN(rowDate.getTime())) return;
        if (rowDate < range.start || rowDate > range.end) return;
        total += this.parseHours(row);
      });
      return total;
    },
    async fetchOrgMonthLoggedHoursDecimal(monthKey) {
      const range = monthDateRangeFromKey(monthKey);
      if (!range) return 0;
      const query = new URLSearchParams({
        start_date: this.formatApiDateForTimesheetQuery(range.start),
        end_date: this.formatApiDateForTimesheetQuery(range.end),
      });
      const scopedUserId = this.effectiveTimesheetUserId;
      if (scopedUserId != null) {
        query.set("user_id", String(scopedUserId));
      }
      const timesheetsRes = await axios.get(
        `api/users/timesheets/get-timesheet/?${query.toString()}`,
        {timeout: 25000}
      );
      const timesheets = parseTimesheetApiList(timesheetsRes.data);
      return this.sumTimesheetHoursInMonthRange(timesheets, monthKey);
    },
    async loadHistoricFteMonths() {
      if (!this.hasKonekoCap(KONEKO_CAP.konekoOrgBudgetEditor)) return;
      const token = ++this.historicFteLoadToken;
      this.historicFteLoading = true;
      this.historicFteError = "";
      const monthKeys = listRollingMonthKeys(12, new Date());
      const currentKey = dateToMonthKey(new Date());
      try {
        if (
          this.calendarMonthKey &&
          monthKeys.includes(this.calendarMonthKey) &&
          (this.membersData || []).length
        ) {
          this.syncHistoricFteFromLoadedMonth();
        }
        const batchSize = 2;
        for (let i = 0; i < monthKeys.length; i += batchSize) {
          if (token !== this.historicFteLoadToken) return;
          const batch = monthKeys.slice(i, i + batchSize);
          for (const monthKey of batch) {
            if (token !== this.historicFteLoadToken) return;
            if (
              monthKey === this.calendarMonthKey &&
              (this.membersData || []).length
            ) {
              continue;
            }
            if (isFutureKonekoCalendarMonthKey(monthKey)) {
              this.$set(this.historicFteByMonth, monthKey, 0);
              continue;
            }
            const cached = this.readHistoricFteHoursCache(monthKey);
            if (cached != null) {
              this.$set(this.historicFteByMonth, monthKey, cached);
              continue;
            }
            const hoursDec = await this.fetchOrgMonthLoggedHoursDecimal(
              monthKey
            );
            if (token !== this.historicFteLoadToken) return;
            this.$set(this.historicFteByMonth, monthKey, hoursDec);
            this.writeHistoricFteHoursCache(monthKey, hoursDec);
          }
        }
        if (token !== this.historicFteLoadToken) return;
        if (monthKeys.includes(currentKey)) {
          const useLoaded =
            this.calendarMonthKey === currentKey &&
            (this.membersData || []).length;
          const hoursDec = useLoaded
            ? this.sumOrgMonthLoggedHours()
            : await this.fetchOrgMonthLoggedHoursDecimal(currentKey);
          if (token !== this.historicFteLoadToken) return;
          this.$set(this.historicFteByMonth, currentKey, hoursDec);
        }
      } catch (e) {
        if (token !== this.historicFteLoadToken) return;
        this.historicFteError =
          (e && e.message) || "Failed to load historic FTE data";
      } finally {
        if (token === this.historicFteLoadToken) {
          this.historicFteLoading = false;
        }
      }
    },
    getProjectBudgetBandFromMap(projectName) {
      const byMonth = this.currentMonthlyBudgetMap || {};
      const needle = normalizeProjectLookupKey(projectName);
      if (!needle) return null;
      const keys = Object.keys(byMonth);
      const exact = keys.find((k) => normalizeProjectLookupKey(k) === needle);
      if (exact != null) return byMonth[exact];
      const needleLower = needle.toLowerCase();
      const ci = keys.find(
        (k) => normalizeProjectLookupKey(k).toLowerCase() === needleLower
      );
      if (ci != null) return byMonth[ci];
      const needleLoose = normalizeProjectLooseKey(needle);
      const loose = keys.find(
        (k) => normalizeProjectLooseKey(k) === needleLoose
      );
      if (loose != null) return byMonth[loose];
      const canonNeedle = canonicalizeKonekoProjectName(projectName) || needle;
      const alias = keys.find(
        (k) =>
          (canonicalizeKonekoProjectName(k) || normalizeProjectLookupKey(k)) ===
          canonNeedle
      );
      return alias != null ? byMonth[alias] : null;
    },
    onMonthChange(monthKey) {
      const q = {...this.$route.query, month: monthKey};
      persistKonekoSelectedMonthKey(monthKey);
      this.$router.replace({query: q}).catch(() => {});
    },
    initKonekoCalendarMonthFromRoute() {
      const queryMonth = String(this.$route.query.month || "").trim();
      const storedMonth = readKonekoSelectedMonthKey();
      let monthKey = parseMonthKey(queryMonth) ? queryMonth : storedMonth;
      if (!parseMonthKey(monthKey)) {
        monthKey = defaultKonekoCalendarMonthKey();
      }
      this.selectedMonthKey = monthKey;
      persistKonekoSelectedMonthKey(monthKey);
      if (!parseMonthKey(queryMonth) || queryMonth !== monthKey) {
        this.onMonthChange(monthKey);
      }
    },
    setKonekoCalendarMonth(monthKey) {
      if (!parseMonthKey(monthKey)) return;
      this.selectedMonthKey = monthKey;
      persistKonekoSelectedMonthKey(monthKey);
      this.onMonthChange(monthKey);
    },
    goToPreviousKonekoMonth() {
      this.setKonekoCalendarMonth(
        shiftKonekoMonthKey(this.calendarMonthKey, -1)
      );
    },
    goToNextKonekoMonth() {
      this.setKonekoCalendarMonth(
        shiftKonekoMonthKey(this.calendarMonthKey, 1)
      );
    },
    getProjectSnapshotBudgetBaseline(projectName) {
      const k = normalizeProjectLookupKey(projectName);
      const v = k ? this.projectSnapshotBudgetBaselineByProject[k] : "";
      if (v === "high" || v === "low") return v;
      const leg = this.legacyProjectSnapshotBudgetBaseline;
      if (leg === "high" || leg === "low") return leg;
      return "low";
    },
    setProjectSnapshotBudgetBaselineForProject(projectName, v) {
      if (v !== "high" && v !== "low") return;
      const k = normalizeProjectLookupKey(projectName);
      if (!k) return;
      this.$set(this.projectSnapshotBudgetBaselineByProject, k, v);
    },
    /**
     * Month usage label for snapshot rows (computed on the mixin so `this` is correct).
     * Equal low/high → one denominator, "budget cap". Distinct low/high → respects baseline.
     */
    computeSnapshotUsage(project) {
      const bar = project && project.budgetBar;
      if (!bar) return {label: ""};
      const current = Number(bar.current);
      if (!Number.isFinite(current) || current < 0) return {label: ""};
      const baseline = this.getProjectSnapshotBudgetBaseline(
        project && project.name
      );
      const {denom, suffix} = snapshotBudgetDenominator(bar, baseline);
      if (denom == null || denom <= 0) return {label: ""};
      const pct = Math.round((current / denom) * 100);
      return {label: `${pct}% of ${suffix}`};
    },
    formatDate(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}.${m}.${day}`;
    },
    /** Report-style DD.MM.YY used on member cards. */
    formatReportDdMmYy(d) {
      if (!d || Number.isNaN(d.getTime())) return "—";
      const yy = String(d.getFullYear() % 100).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${day}.${month}.${yy}`;
    },
    memberHeadlineHours(m) {
      const rep =
        konekoReportingElsewhereUsernames.includes(
          canonicalizeKonekoUsername(m.username)
        ) && hoursToDecimal(m.loggedHours) <= 0;
      if (rep) return "0:00";
      const d = hoursToDecimal(m.loggedHours);
      return d > 0 ? String(m.loggedHours).trim() : "0:00";
    },
    memberHeadlineLimitClause(m) {
      const u = canonicalizeKonekoUsername(m.username);
      const repElse =
        konekoReportingElsewhereUsernames.includes(u) &&
        hoursToDecimal(m.loggedHours) <= 0;
      if (repElse) return " (Reporting Elsewhere)";
      const minT = hoursToDecimal(m.targetMinHours || m.targetHours);
      const maxT = hoursToDecimal(m.targetMaxHours || m.targetHours);
      if (maxT > 0) {
        const rangeLabel =
          minT > 0 && Math.abs(minT - maxT) > 0.001
            ? `${decimalToHHMM(minT)}-${decimalToHHMM(maxT)}`
            : decimalToHHMM(maxT);
        return ` (${this.memberProgressPercent(m)}% of ${rangeLabel})`;
      }
      return "";
    },
    memberHasUnknownProject(m) {
      return (m.allocations || []).some((a) => a.project === "unknown");
    },
    memberBreakdownMonthTotalLabel(member) {
      const d = hoursToDecimal(member && member.loggedHours);
      return d > 0 ? String(member.loggedHours).trim() : "0:00";
    },
    memberBreakdownCapTotalLabel(member) {
      if (!member) return "—";
      const minT = hoursToDecimal(member.targetMinHours);
      const maxT = hoursToDecimal(member.targetMaxHours);
      if (maxT <= 0) return "—";
      if (minT > 0 && Math.abs(minT - maxT) > 0.001) {
        return `${member.targetMinHours}–${member.targetMaxHours}`;
      }
      return String(member.targetMaxHours).trim();
    },
    /** True when month hours on a line exceed personal low/high/target on `personalBudgetBar`. */
    personalAllocationOverBudget(line) {
      const b = line && line.personalBudgetBar;
      if (!b) return false;
      return isKonekoBudgetBarOverrun(b.current, b);
    },
    /** UI layer: never show literal `0:00` (rows with both zeros are still pruned in data/computed). */
    formatHoursCell(value) {
      return hoursToDecimal(value) > 0 ? String(value).trim() : "—";
    },
    formatSignedHours(hoursDecimal) {
      const sign = hoursDecimal > 0 ? "+" : "";
      return `${sign}${decimalToHHMM(hoursDecimal)}`;
    },
    dateFromRow(row) {
      const raw =
        row.log_date || row.timer_log_date || row.created_at || row.updated_at;
      if (!raw) return null;
      const iso = preserveLogDateString(raw);
      if (iso) {
        const [y, mo, d] = iso.split("-").map((x) => parseInt(x, 10));
        return new Date(y, mo - 1, d);
      }
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? null : d;
    },
    inWeek(d) {
      if (!d || Number.isNaN(d.getTime())) return false;
      return d >= this.weekStart && d <= this.weekEnd;
    },
    inMonth(d) {
      if (!d || Number.isNaN(d.getTime())) return false;
      return d >= this.timesheetMonthStart && d <= this.timesheetMonthEnd;
    },
    parseHours(row) {
      const value =
        row.working_hours ??
        row.hours_with_timer ??
        row.hours ??
        row.log_hour ??
        0;
      const numeric = parseFloat(value);
      return Number.isFinite(numeric) ? numeric : 0;
    },
    resolveUserId(row) {
      return row.user_id ?? row.user ?? row.userId ?? null;
    },
    resolveProjectId(row) {
      return row.project_id ?? row.project ?? row.projectId ?? null;
    },
    cacheDateKey() {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    cacheKey() {
      const scope = this.loadTimesheetsOrgWide
        ? "admin"
        : this.globalViewAsPlanningKey ||
          this.requestedMember ||
          this.currentUserKey ||
          "self";
      // Bump version when display rules change so stale localStorage is not shown as-is.
      return `konekoA:v27:${scope}:${this.calendarMonthKey}:ts:${
        this.timesheetMonthKey
      }:${this.cacheDateKey()}`;
    },
    readCache() {
      try {
        const raw = localStorage.getItem(this.cacheKey());
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.membersData || !parsed.projectBudgetsData)
          return null;
        return parsed;
      } catch {
        return null;
      }
    },
    writeCache() {
      try {
        localStorage.setItem(
          this.cacheKey(),
          JSON.stringify({
            membersData: this.membersData,
            projectBudgetsData: this.projectBudgetsData,
            updatedAt: new Date().toISOString(),
          })
        );
      } catch {
        // ignore cache write failures
      }
    },
    teamMembersQueryForWorkload() {
      const q =
        this.signedInIsKonekoAdmin ||
        this.hasKonekoCap(KONEKO_CAP.konekoOrgBudgetEditor)
          ? "?include_not_working=1"
          : "";
      return q;
    },
    async fetchWorkloadTeamMembers() {
      const q = this.teamMembersQueryForWorkload();
      let rows = [];
      try {
        const {data} = await axios.get(`api/users/team-members/${q}`, {
          timeout: 25000,
        });
        if (Array.isArray(data)) rows = data;
        else if (data && Array.isArray(data.results)) rows = data.results;
      } catch {
        /* fall through to list */
      }
      if (!rows.length) {
        try {
          const {data} = await axios.get(`api/users/team-members/list/`, {
            timeout: 25000,
          });
          if (Array.isArray(data)) rows = data;
        } catch {
          /* ignore */
        }
      }
      this.konekoTeamRosterMetaByUsername = buildKonekoTeamRosterMetaByUsername(
        rows
      );
      return rows;
    },
    async fetchTimesheetRowsForWorkload(monthStart, monthEnd, memberRows) {
      const debug = {
        monthStart,
        monthEnd,
        scopedUserId: null,
        rangeCount: 0,
        broadCount: 0,
        timerCount: 0,
        perUserCount: 0,
        futureMonthSkip: false,
      };
      const scopedUserId = this.timesheetQueryUserId(memberRows);
      debug.scopedUserId = scopedUserId;

      /** Future months have no timesheets yet — avoid broad/unfiltered API fallbacks. */
      if (this.isFutureCalendarMonth) {
        const q = new URLSearchParams({
          start_date: monthStart,
          end_date: monthEnd,
        });
        if (scopedUserId != null) q.set("user_id", String(scopedUserId));
        const suffix = q.toString() ? `?${q.toString()}` : "";
        const res = await axios.get(
          `api/users/timesheets/get-timesheet/${suffix}`,
          {timeout: 20000}
        );
        const merged = dedupeTimesheetRows(parseTimesheetApiList(res.data));
        debug.futureMonthSkip = true;
        debug.rangeCount = merged.length;
        this.lastTimesheetFetchDebug = debug;
        return merged;
      }

      const fetchMonthly = async (userId, useDateFilter) => {
        const q = new URLSearchParams();
        if (useDateFilter) {
          q.set("start_date", monthStart);
          q.set("end_date", monthEnd);
        }
        if (userId != null) q.set("user_id", String(userId));
        const suffix = q.toString() ? `?${q.toString()}` : "";
        const res = await axios.get(
          `api/users/timesheets/get-timesheet/${suffix}`,
          {timeout: useDateFilter ? 20000 : 45000}
        );
        let rows = parseTimesheetApiList(res.data);
        if (!useDateFilter) {
          rows = rows.filter((row) =>
            timesheetRowInIsoRange(row, monthStart, monthEnd)
          );
        }
        return rows;
      };

      const fetchTimers = async (userId) => {
        const q = new URLSearchParams({
          start_date: monthStart,
          end_date: monthEnd,
        });
        if (userId != null) q.set("user_id", String(userId));
        const res = await axios.get(
          `api/users/timers/filtered-data/?${q.toString()}`,
          {timeout: 20000}
        );
        return parseTimesheetApiList(res.data)
          .filter((row) => timesheetRowInIsoRange(row, monthStart, monthEnd))
          .map(normalizeTimerRowToTimesheetRow);
      };

      const loadForUser = async (userId) => {
        let rows = await fetchMonthly(userId, true);
        if (!rows.length) rows = await fetchTimers(userId);
        // Only allow unfiltered fallback for a single scoped user — never org-wide.
        if (!rows.length && userId != null) {
          rows = await fetchMonthly(userId, false);
        }
        return rows;
      };

      let merged = dedupeTimesheetRows(await loadForUser(scopedUserId));
      debug.rangeCount = merged.length;
      if (!merged.length) {
        const timers = dedupeTimesheetRows(await fetchTimers(scopedUserId));
        debug.timerCount = timers.length;
        merged = timers;
      }

      if (!merged.length && scopedUserId == null) {
        const userIds = [];
        const seen = new Set();
        for (const m of memberRows || []) {
          const uid = resolveTeamRowUserId(m) ?? m.id;
          if (uid == null || uid <= 0 || seen.has(uid)) continue;
          seen.add(uid);
          userIds.push(uid);
        }
        const batchSize = 4;
        for (let i = 0; i < userIds.length; i += batchSize) {
          const batch = userIds.slice(i, i + batchSize);
          const parts = await Promise.all(
            batch.map((uid) => loadForUser(uid).catch(() => []))
          );
          parts.forEach((part) => {
            merged.push(...part);
          });
        }
        merged = dedupeTimesheetRows(merged);
        debug.perUserCount = merged.length;
      }

      this.lastTimesheetFetchDebug = debug;
      return merged;
    },
    async ensureKonekoProjectsCatalog({force = false} = {}) {
      const existing = Array.isArray(this.konekoProjectsList)
        ? this.konekoProjectsList
        : [];
      if (!force && existing.length) return existing;
      const sessionRows = readKonekoProjectsCatalogFromSession();
      if (!force && sessionRows.length) {
        this.konekoProjectsList = sessionRows;
        return sessionRows;
      }
      try {
        const {data} = await axios.get("api/projects/list/", {timeout: 15000});
        const projects = normalizeKonekoProjectsApiRows(data);
        if (projects.length) {
          this.konekoProjectsList = projects;
          writeKonekoProjectsCatalogToSession(projects);
        }
        return projects;
      } catch {
        if (sessionRows.length) {
          this.konekoProjectsList = sessionRows;
          return sessionRows;
        }
        return existing;
      }
    },
    scheduleHistoricFteLoad() {
      if (!this.hasKonekoCap(KONEKO_CAP.konekoOrgBudgetEditor)) return;
      if (this._historicFteLoadScheduled) return;
      this._historicFteLoadScheduled = true;
      window.setTimeout(() => {
        this._historicFteLoadScheduled = false;
        this.loadHistoricFteMonths();
      }, 800);
    },
    applyKonekoDisplayCache(cached) {
      if (!cached) return;
      this.membersData = pruneMembersForDisplay(
        cached.membersData,
        this.calendarMonthKey,
        this.konekoTeamRosterMetaByUsername
      );
      this.projectBudgetsData = pruneProjectsForDisplay(
        cached.projectBudgetsData
      );
      this.lastUpdatedAt = cached.updatedAt || "";
    },
    async loadKonekoAData(forceRefresh = false) {
      let cached = null;
      if (!forceRefresh) cached = this.readCache();
      if (cached) {
        this.applyKonekoDisplayCache(cached);
      }
      this.loading = true;
      this.loadError = "";
      await this.ensureKonekoProjectsCatalog();
      try {
        const profileUser = profileUsernameFromJwt(this.userProfileData);
        const mayPrefetch =
          konekoABudgetBackendEnabled &&
          (this.signedInIsKonekoAdmin ||
            this.hasKonekoCap(KONEKO_CAP.konekoTeamPersonalBudgets) ||
            (profileUser && isKonekoBudgetBackendEnabledForUser(profileUser)));
        const monthStart = this.formatDate(this.timesheetMonthStart).replaceAll(
          ".",
          "-"
        );
        const monthEnd = this.formatDate(this.timesheetMonthEnd).replaceAll(
          ".",
          "-"
        );
        const parallelLoads = [
          this.fetchWorkloadTeamMembers(),
          axios.get("api/projects/list/", {timeout: 15000}),
          import("@/services/konekoProjectRenameApi").then((m) =>
            m.hydrateKonekoProjectNameAliases()
          ),
        ];
        if (mayPrefetch) {
          parallelLoads.push(
            this.prefetchKonekoBudgetApiForMonth(this.calendarMonthKey)
          );
        }
        const parallelResults = await Promise.all(parallelLoads);
        const members = parallelResults[0];
        this.konekoTeamRosterMetaByUsername = buildKonekoTeamRosterMetaByUsername(
          members
        );
        const projectsResult = parallelResults[1];
        const projects = normalizeKonekoProjectsApiRows(projectsResult.data);
        if (projects.length) {
          this.konekoProjectsList = projects;
          writeKonekoProjectsCatalogToSession(projects);
        }
        const timesheets = await this.fetchTimesheetRowsForWorkload(
          monthStart,
          monthEnd,
          members
        );
        this.lastTimesheetRowCount = timesheets.length;
        this.konekoWorkloadCache = {timesheets, members, projects};
        this.buildRealData(timesheets, members, projects);
        this.lastUpdatedAt = new Date().toISOString();
        this.writeCache();
        this.syncHistoricFteFromLoadedMonth();
        this.loadMyMemberTargetsDraft();
        if (timesheets.length === 0 && !this.isFutureCalendarMonth) {
          const scope = this.loadTimesheetsOrgWide ? "org-wide" : "your user";
          const dbg = this.lastTimesheetFetchDebug;
          const detail = dbg
            ? ` (range=${dbg.rangeCount}, broad=${dbg.broadCount}, timers=${dbg.timerCount}, per-user=${dbg.perUserCount})`
            : "";
          this.loadError = `No timesheet rows for ${this.timesheetMonthKey} (${monthStart}–${monthEnd}, ${scope})${detail}. Confirm you are on production API and logged time exists in NekoHR Timesheet/Timer for that month, then click Refresh data.`;
        } else {
          this.loadError = "";
        }
      } catch (e) {
        if (cached) {
          this.membersData = pruneMembersForDisplay(
            cached.membersData,
            this.calendarMonthKey,
            this.konekoTeamRosterMetaByUsername
          );
          this.projectBudgetsData = pruneProjectsForDisplay(
            cached.projectBudgetsData
          );
          this.lastUpdatedAt = cached.updatedAt || "";
        }
        await this.ensureKonekoProjectsCatalog();
        const detail = (e && e.message) || "Unknown error";
        this.loadError = `Failed to load Koneko A data from backend: ${detail}`;
      } finally {
        this.loading = false;
        this.scheduleHistoricFteLoad();
      }
    },
    hasKonekoCap(cap) {
      return Boolean(this.konekoCapabilities && this.konekoCapabilities[cap]);
    },
    timesheetQueryUserId(memberRows) {
      if (this.loadTimesheetsOrgWide) return null;
      if (this.viewAsPreviewActive && this.globalViewAsPlanningKey) {
        const id = this.resolvePlanningKeyToUserId(
          this.globalViewAsPlanningKey,
          memberRows
        );
        if (id != null) return id;
      }
      const u = this.userProfileData;
      return u && u.user_id != null ? u.user_id : null;
    },
    /** Map planning roster key → NekoHR user id for timesheet API scoping. */
    resolvePlanningKeyToUserId(planningKey, memberRows) {
      const key = String(planningKey || "").trim();
      if (!key) return null;
      const roster = [];
      if (Array.isArray(memberRows)) roster.push(...memberRows);
      const cacheMembers =
        this.konekoWorkloadCache && this.konekoWorkloadCache.members;
      if (Array.isArray(cacheMembers)) roster.push(...cacheMembers);
      (this.membersData || []).forEach((m) => {
        if (m && m.id != null) {
          roster.push({
            id: m.id,
            username: m.username,
            full_name: m.name,
          });
        }
      });
      for (const m of roster) {
        const uname = m.username || m.user_name || "";
        if (konekoPlanningKeyMatchesMemberRow(uname, key)) {
          const id = m.id != null ? m.id : m.user_id;
          if (id != null) return id;
        }
      }
      return null;
    },
    onKonekoVisibilitySettingsSaved() {
      bumpKonekoVisibilityOverrides();
    },
    refreshNow() {
      this.loadKonekoAData(true);
    },
    rebuildWorkloadFromCache() {
      const cache = this.konekoWorkloadCache;
      if (!cache || !Array.isArray(cache.timesheets)) return;
      this.buildRealData(
        cache.timesheets,
        cache.members || [],
        cache.projects || []
      );
      this.writeCache();
    },
    refreshKonekoBudgetHistoryPanel() {
      const panel = this.$refs && this.$refs.konekoBudgetHistory;
      if (panel && typeof panel.refresh === "function") {
        panel.refresh();
      }
    },
    timesheetDateForApi(value) {
      const iso = preserveLogDateString(value);
      if (iso) return iso;
      if (!value) return null;
      const d = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(d.getTime())) return null;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    projectNameFromId(projectId) {
      const id = Number(projectId);
      const opt = (this.konekoProjectOptions || []).find(
        (p) => Number(p.id) === id
      );
      if (opt) return opt.name;
      const row = (this.konekoProjectsList || []).find(
        (p) => Number(p.id) === id
      );
      return row ? row.project_name || row.name || "" : "";
    },
    signedInKonekoEditor() {
      const u = this.userProfileData;
      const username = canonicalizeKonekoUsername(u && u.username);
      const displayName =
        (u && (u.full_name || u.name || u.username)) || username || "";
      return {username, displayName};
    },
    enrichKonekoTimesheetEntry(entry, rawRow) {
      const ownerKey = canonicalizeKonekoUsername(
        entry.memberUsername || entry.username
      );
      let attribution =
        timesheetRowEditorFromApi(rawRow) ||
        parseEditedByFromNotes(
          rawRow &&
            (rawRow.log_description ||
              rawRow.task_description ||
              rawRow.description)
        ) ||
        getKonekoTimesheetEditAttribution(entry.timesheetId);
      const editedByUsername = attribution ? attribution.editorUsername : "";
      const editedByLabel = attribution ? attribution.editorLabel : "";
      const editedBySelf =
        Boolean(editedByUsername) &&
        Boolean(ownerKey) &&
        editedByUsername === ownerKey;
      return {
        ...entry,
        editedByUsername,
        editedByLabel,
        editedBySelf,
        editedAt:
          attribution && attribution.editedAt ? attribution.editedAt : null,
      };
    },
    recordKonekoTimesheetEdit(entry) {
      const editor = this.signedInKonekoEditor();
      if (!editor.username || !entry || entry.timesheetId == null) return;
      recordKonekoTimesheetEditAttribution(entry.timesheetId, editor);
    },
    findRawTimesheetRow(timesheetId) {
      const cache = this.konekoWorkloadCache;
      if (!cache || !Array.isArray(cache.timesheets)) return null;
      return (
        cache.timesheets.find((r) => Number(r.id) === Number(timesheetId)) ||
        null
      );
    },
    buildKonekoTimesheetUpdatePayload(raw, entry, patch = {}) {
      const logDate =
        preserveLogDateString(raw.log_date) ||
        preserveLogDateString(raw.timer_log_date) ||
        this.timesheetDateForApi(entry.date);
      const hours =
        patch.working_hours != null
          ? patch.working_hours
          : raw.working_hours != null
          ? raw.working_hours
          : raw.hours != null
          ? raw.hours
          : entry.hoursDec;
      const editor = this.signedInKonekoEditor();
      const descRaw =
        patch.log_description != null
          ? patch.log_description
          : raw.log_description || raw.task_description || entry.notes || "";
      const desc =
        editor.username && !String(descRaw).includes("[Koneko edited by")
          ? appendKonekoEditedByNote(descRaw, editor.username)
          : String(descRaw || "").trim();
      const userId = normalizeFkId(raw.user_id);
      const projectId =
        patch.project_id != null
          ? patch.project_id
          : normalizeFkId(raw.project_id);
      return {
        ...raw,
        id: raw.id,
        user_id: userId,
        project_id: projectId,
        log_date: logDate,
        timer_log_date: logDate,
        log_description: desc,
        task_description: desc,
        working_hours: hours,
        hours,
        hours_with_timer:
          raw.hours_with_timer != null ? raw.hours_with_timer : hours,
      };
    },
    canEditKonekoTimesheetEntry(entry) {
      return this.canReassignKonekoEntry(entry);
    },
    canReassignKonekoEntry(entry) {
      if (!entry || entry.timesheetId == null) return false;
      if (this.hasKonekoCap(KONEKO_CAP.konekoReassignTimesheet)) return true;
      const scoped = this.effectiveTimesheetUserId;
      const uid =
        scoped != null
          ? String(scoped)
          : this.userProfileData && this.userProfileData.user_id != null
          ? String(this.userProfileData.user_id)
          : "";
      return Boolean(uid && String(entry.userId) === uid);
    },
    async refetchKonekoTimesheets() {
      const monthStart = this.formatDate(this.timesheetMonthStart).replaceAll(
        ".",
        "-"
      );
      const monthEnd = this.formatDate(this.timesheetMonthEnd).replaceAll(
        ".",
        "-"
      );
      const cache = this.konekoWorkloadCache || {};
      const members =
        (cache.members && cache.members.length
          ? cache.members
          : await this.fetchWorkloadTeamMembers()) || [];
      const timesheets = await this.fetchTimesheetRowsForWorkload(
        monthStart,
        monthEnd,
        members
      );
      this.lastTimesheetRowCount = timesheets.length;
      this.konekoWorkloadCache = {
        timesheets,
        members: cache.members || [],
        projects: cache.projects || this.konekoProjectsList || [],
      };
      this.buildRealData(
        timesheets,
        this.konekoWorkloadCache.members,
        this.konekoWorkloadCache.projects
      );
      this.lastUpdatedAt = new Date().toISOString();
      this.writeCache();
    },
    reassignKonekoTimesheetEntry(
      entry,
      newProjectId,
      newProjectName,
      meta = {}
    ) {
      if (!this.canReassignKonekoEntry(entry)) return;
      const targetId = Number(newProjectId);
      if (!Number.isFinite(targetId) || targetId <= 0) return;
      if (Number(entry.projectId) === targetId) return;

      const fromProject =
        meta.fromProject ||
        this.projectNameFromId(entry.projectId) ||
        "previous project";
      const toProject =
        newProjectName || this.projectNameFromId(targetId) || "new project";

      this.konekoReassignConfirm = {
        show: true,
        entry,
        newProjectId: targetId,
        newProjectName: toProject,
        fromProject,
        memberKey: meta.memberKey ? String(meta.memberKey) : "",
        hoursLabel: entry.hours || "—",
        dateLabel: entry.dateLabel || "—",
      };
    },
    cancelKonekoTimesheetReassign() {
      if (this.timesheetReassignSavingId) return;
      this.konekoReassignConfirm = {
        show: false,
        entry: null,
        newProjectId: null,
        newProjectName: "",
        fromProject: "",
        memberKey: "",
        hoursLabel: "",
        dateLabel: "",
      };
    },
    async confirmKonekoTimesheetReassign() {
      const c = this.konekoReassignConfirm;
      if (!c || !c.show || !c.entry) return;
      const entry = c.entry;
      const targetId = Number(c.newProjectId);
      const fromProject = c.fromProject || "previous project";
      const toProject = c.newProjectName || this.projectNameFromId(targetId);
      const hoursLabel = c.hoursLabel || entry.hours || "—";
      const dateLabel = c.dateLabel || entry.dateLabel || "—";
      const memberKey = c.memberKey || "";

      const cache = this.konekoWorkloadCache;
      if (!cache || !Array.isArray(cache.timesheets)) {
        this.konekoBudgetApiError =
          "Reload timesheet data before reassigning entries.";
        return;
      }
      const raw = cache.timesheets.find(
        (r) => Number(r.id) === Number(entry.timesheetId)
      );
      if (!raw) {
        this.konekoBudgetApiError =
          "Timesheet row not found — click Refresh data.";
        return;
      }
      const payload = this.buildKonekoTimesheetUpdatePayload(raw, entry, {
        project_id: targetId,
      });
      this.timesheetReassignSavingId = entry.timesheetId;
      this.konekoBudgetApiError = "";
      this.konekoReassignNotice = "";
      try {
        await updateKonekoTimesheetEntry(entry.timesheetId, payload);
        this.recordKonekoTimesheetEdit(entry);
        await this.refetchKonekoTimesheets();
        this.cancelKonekoTimesheetReassign();
        this.konekoReassignNotice = `Moved ${hoursLabel} on ${dateLabel} from “${fromProject}” to “${toProject}”. Open “${toProject}” under that person to see the line.`;
        if (memberKey && toProject) {
          const expandKey = this.teamBudgetProjectKey(memberKey, toProject);
          if (!(this.teamBudgetProjectExpandKeys || []).includes(expandKey)) {
            this.teamBudgetProjectExpandKeys = [
              ...(this.teamBudgetProjectExpandKeys || []),
              expandKey,
            ];
          }
        }
      } catch (e) {
        this.konekoBudgetApiError = String(
          (e &&
            e.response &&
            e.response.data &&
            JSON.stringify(e.response.data)) ||
            (e && e.message) ||
            "Failed to reassign timesheet"
        );
      } finally {
        this.timesheetReassignSavingId = null;
      }
    },
    isEditingTimesheetHours(entry) {
      return (
        entry &&
        entry.timesheetId != null &&
        Number(this.timesheetHoursEdit.timesheetId) ===
          Number(entry.timesheetId)
      );
    },
    startKonekoTimesheetHoursEdit(entry) {
      if (!this.canEditKonekoTimesheetEntry(entry)) return;
      this.timesheetHoursEdit = {
        timesheetId: entry.timesheetId,
        value: entry.hours || "0:00",
      };
    },
    cancelKonekoTimesheetHoursEdit() {
      if (this.timesheetHoursSavingId) return;
      this.timesheetHoursEdit = {timesheetId: null, value: ""};
    },
    async commitKonekoTimesheetHoursEdit(entry) {
      if (!entry || !this.isEditingTimesheetHours(entry)) return;
      const formatted = HHMMFormat(String(this.timesheetHoursEdit.value || ""));
      const hoursDec = convertHMSFormatInputToHours(formatted);
      if (!Number.isFinite(hoursDec) || hoursDec < 0) {
        this.konekoBudgetApiError =
          "Enter hours as H:MM or minutes (e.g. 1:30 or 90).";
        return;
      }
      const prevDec = Number(entry.hoursDec);
      if (Number.isFinite(prevDec) && Math.abs(prevDec - hoursDec) < 0.01) {
        this.cancelKonekoTimesheetHoursEdit();
        return;
      }
      const raw = this.findRawTimesheetRow(entry.timesheetId);
      if (!raw) {
        this.konekoBudgetApiError =
          "Timesheet row not found — click Refresh data.";
        return;
      }
      const payload = this.buildKonekoTimesheetUpdatePayload(raw, entry, {
        working_hours: hoursDec,
      });
      this.timesheetHoursSavingId = entry.timesheetId;
      this.konekoBudgetApiError = "";
      const editor = this.signedInKonekoEditor();
      const editorLabel = formatKonekoEditorLabel(
        editor.username,
        editor.displayName
      );
      try {
        await updateKonekoTimesheetEntry(entry.timesheetId, payload);
        this.recordKonekoTimesheetEdit(entry);
        await this.refetchKonekoTimesheets();
        this.cancelKonekoTimesheetHoursEdit();
        this.konekoReassignNotice = `Updated ${
          entry.dateLabel || "line"
        } to ${decimalToHHMM(hoursDec)} for ${
          entry.memberUsername || "member"
        } — edited by ${editorLabel}.`;
      } catch (e) {
        this.konekoBudgetApiError = String(
          (e &&
            e.response &&
            e.response.data &&
            JSON.stringify(e.response.data)) ||
            (e && e.message) ||
            "Failed to update timesheet hours"
        );
      } finally {
        this.timesheetHoursSavingId = null;
      }
    },
    buildRealData(timesheets, members, projects) {
      const rosterMeta = this.konekoTeamRosterMetaByUsername || {};
      const budgetMonthKey = `${this.monthStart.getFullYear()}-${String(
        this.monthStart.getMonth() + 1
      ).padStart(2, "0")}`;
      const memberExcludedForMonth = (username, name) =>
        konekoMemberExcludedForMonth(
          budgetMonthKey,
          username,
          name,
          rosterMeta
        );

      const memberById = {};
      members.forEach((m) => {
        const userId = resolveTeamRowUserId(m) ?? m.id;
        memberById[userId] = {
          id: userId,
          username: (m.username || m.user_name || "").toLowerCase(),
          name: m.full_name || m.username || `User ${userId}`,
        };
        if (m.id != null && m.id !== userId) {
          memberById[m.id] = memberById[userId];
        }
      });
      // Ensure current authenticated user is still name-resolvable even when member list endpoint is restricted.
      if (
        this.userProfileData &&
        this.userProfileData.user_id &&
        !memberById[this.userProfileData.user_id]
      ) {
        memberById[this.userProfileData.user_id] = {
          id: this.userProfileData.user_id,
          username: (this.userProfileData.username || "").toLowerCase(),
          name:
            this.userProfileData.full_name ||
            this.userProfileData.username ||
            `User ${this.userProfileData.user_id}`,
        };
      }
      const projectById = {};
      projects.forEach((p) => {
        projectById[p.id] = p.project_name || p.name || `Project ${p.id}`;
      });

      const perMember = {};
      const perProject = {};
      // Include all projects up-front so admin gets complete project list, even when hours are zero.
      Object.values(projectById).forEach((projectName) => {
        if (!perProject[projectName])
          perProject[projectName] = {week: 0, month: 0, target: 0};
      });
      // Include all members up-front when building org-wide aggregates.
      if (this.loadTimesheetsOrgWide) {
        Object.values(memberById).forEach((member) => {
          if (memberExcludedForMonth(member.username, member.name)) return;
          perMember[member.id] = {
            username: member.username,
            name: member.name,
            targetHours: "0:00",
            loggedHours: "0:00",
            lastLogged: "-",
            daysSinceDailyReport: "N/A",
            _monthTotal: 0,
            _alloc: {},
          };
        });
      }

      timesheets.forEach((row) => {
        const userId = this.resolveUserId(row);
        const projectId = this.resolveProjectId(row);
        if (!userId) return;
        const member = memberById[userId];
        if (!member) {
          memberById[userId] = {
            id: userId,
            username: `user_${userId}`,
            name: `User ${userId}`,
          };
        }
        const resolvedMember = memberById[userId];
        if (
          memberExcludedForMonth(resolvedMember.username, resolvedMember.name)
        ) {
          return;
        }
        const projectName = projectById[projectId] || "unknown";
        const logged = this.parseHours(row);
        const rowDate = this.dateFromRow(row);
        const inWeek = this.inWeek(rowDate);
        const inMonth = this.inMonth(rowDate);
        if (!inWeek && !inMonth) return;

        if (!perMember[userId]) {
          perMember[userId] = {
            username: resolvedMember.username,
            name: resolvedMember.name,
            targetHours: "0:00",
            loggedHours: "0:00",
            lastLogged: "-",
            daysSinceDailyReport: "N/A",
            _monthTotal: 0,
            _alloc: {},
          };
        }
        if (!perMember[userId]._alloc[projectName]) {
          perMember[userId]._alloc[projectName] = {
            project: projectName,
            weekLoggedRaw: 0,
            monthLoggedRaw: 0,
            monthEntries: [],
          };
        }
        if (inWeek)
          perMember[userId]._alloc[projectName].weekLoggedRaw += logged;
        if (inMonth) {
          perMember[userId]._alloc[projectName].monthLoggedRaw += logged;
          perMember[userId]._monthTotal += logged;
          if (
            !Array.isArray(perMember[userId]._alloc[projectName].monthEntries)
          ) {
            perMember[userId]._alloc[projectName].monthEntries = [];
          }
          const entryBase = {
            timesheetId: row.id,
            projectId,
            userId,
            memberUsername: resolvedMember.username,
            date: rowDate,
            dateLabel: rowDate ? this.formatReportDdMmYy(rowDate) : "—",
            hoursDec: logged,
            hours: decimalToHHMM(logged),
            inWeek: Boolean(inWeek),
            notes: timesheetRowNotes(row),
          };
          perMember[userId]._alloc[projectName].monthEntries.push(
            this.enrichKonekoTimesheetEntry(entryBase, row)
          );
        }
        if (!perProject[projectName]) {
          perProject[projectName] = {week: 0, month: 0, target: 0};
        }
        if (inWeek) perProject[projectName].week += logged;
        if (inMonth) perProject[projectName].month += logged;
      });

      if (this.loadTimesheetsOrgWide) {
        injectMissingPlanningRosterMembers(
          perMember,
          budgetMonthKey,
          rosterMeta
        );
      } else if (this.viewAsPreviewActive && this.globalViewAsPlanningKey) {
        ensurePlanningMemberInPerMember(
          perMember,
          memberById,
          this.globalViewAsPlanningKey
        );
      } else {
        ensureAuthenticatedMemberInPerMember(
          perMember,
          memberById,
          this.userProfileData
        );
      }

      const now = new Date();
      const membersData = Object.values(perMember)
        .filter((m) => !memberExcludedForMonth(m.username, m.name))
        .map((m) => {
          const targetCache = this.konekoMemberTargetsApiCacheByMonth;
          let mergedTargets = resolveMemberTargets(
            budgetMonthKey,
            m.username,
            m.name,
            targetCache
          );
          if (!Object.keys(mergedTargets).length) {
            for (const rk of listKonekoAPlanningMemberKeys()) {
              if (konekoPlanningKeyMatchesMemberRow(m.username, rk)) {
                mergedTargets = resolveMemberTargets(
                  budgetMonthKey,
                  rk,
                  "",
                  targetCache
                );
                break;
              }
            }
          }
          if (!Object.keys(mergedTargets).length) {
            for (const rk of listKonekoAPlanningMemberKeys()) {
              const pretty = formatKonekoAPlanningMemberSelectLabel(rk);
              if (
                konekoMemberRowMatchesLookup(m.username, m.name, rk, pretty)
              ) {
                mergedTargets = resolveMemberTargets(
                  budgetMonthKey,
                  rk,
                  "",
                  targetCache
                );
                break;
              }
            }
          }
          const lineNames = resolveCanonicalProjectDisplayNames(
            Object.keys(m._alloc),
            Object.keys(mergedTargets)
          );

          const allocations = lineNames
            .map((projectName) => {
              const base = findMemberAllocBucket(m._alloc, projectName) || {
                project: projectName,
                weekLoggedRaw: 0,
                monthLoggedRaw: 0,
                monthEntries: [],
              };
              const personalBand = getMemberProjectLimitBand(
                budgetMonthKey,
                m.username,
                projectName,
                m.name,
                targetCache
              );
              const target =
                personalBand &&
                (personalBand.target || personalBand.high || personalBand.low)
                  ? String(
                      personalBand.target ||
                        personalBand.high ||
                        personalBand.low
                    )
                  : null;
              const monthLogged = decimalToHHMM(base.monthLoggedRaw);
              const weekLogged = decimalToHHMM(base.weekLoggedRaw);
              let percent = null;
              if (target) {
                const targetDec = hoursToDecimal(target);
                if (targetDec > 0) {
                  percent = `${Math.round(
                    (base.monthLoggedRaw / targetDec) * 100
                  )}%`;
                  perProject[projectName] = perProject[projectName] || {
                    week: 0,
                    month: 0,
                    target: 0,
                  };
                  perProject[projectName].target += targetDec;
                }
              }
              const parsedBand = personalBand
                ? bandToDecimals(personalBand)
                : null;
              return {
                project: projectName,
                weekLogged,
                monthLogged,
                target,
                percent,
                entries: (base.monthEntries || []).slice().sort((a, b) => {
                  const ta = a.date ? new Date(a.date).getTime() : 0;
                  const tb = b.date ? new Date(b.date).getTime() : 0;
                  return tb - ta;
                }),
                // Keep bars visible even when no budget is configured.
                personalBudgetBar: {
                  current: base.monthLoggedRaw,
                  low: parsedBand ? parsedBand.low : null,
                  high: parsedBand ? parsedBand.high : null,
                  target:
                    parsedBand && parsedBand.target != null
                      ? parsedBand.target
                      : target && hoursToDecimal(target) > 0
                      ? hoursToDecimal(target)
                      : null,
                },
              };
            })
            .sort(
              (a, b) =>
                hoursToDecimal(b.monthLogged) - hoursToDecimal(a.monthLogged)
            );
          const latestDates = timesheets
            .filter((r) => {
              const uid = this.resolveUserId(r);
              const known = Object.values(memberById).find(
                (x) => x.username.toLowerCase() === m.username
              );
              return known && uid === known.id;
            })
            .map((r) => this.dateFromRow(r))
            .filter((d) => d && !Number.isNaN(d.getTime()))
            .sort((a, b) => b - a);
          const last = latestDates.length ? latestDates[0] : null;
          const daysSince = last
            ? Math.max(0, Math.floor((now - last) / 86400000))
            : null;
          return {
            username: m.username,
            name: m.name,
            targetHours: "0:00",
            loggedHours: decimalToHHMM(m._monthTotal),
            lastLogged: last ? this.formatReportDdMmYy(last) : "—",
            daysSinceDailyReport:
              daysSince === null
                ? "N/A"
                : daysSince >= 10
                ? "10+"
                : String(daysSince),
            allocations,
          };
        });

      const projectBudgetsData = Object.keys(perProject)
        .sort((a, b) => perProject[b].month - perProject[a].month)
        .map((projectName) => {
          const data = perProject[projectName];
          const band = getKonekoProjectBudgetBand(budgetMonthKey, projectName);
          const fromMonthlyJson = formatProjectBudgetStatus(data.month, band);
          const budgetStatus =
            fromMonthlyJson ??
            (data.target > 0
              ? `${Math.round(
                  (data.month / data.target) * 100
                )}% (member targets)`
              : "FLEX");
          const budgetBar = band
            ? {current: data.month, ...bandToDecimals(band)}
            : null;
          return {
            name: projectName,
            budgetStatus,
            budgetBar,
            weekLogged: decimalToHHMM(data.week),
            monthLogged: decimalToHHMM(data.month),
          };
        });

      this.membersData = pruneMembersForDisplay(
        membersData,
        budgetMonthKey,
        rosterMeta
      );
      this.projectBudgetsData = pruneProjectsForDisplay(projectBudgetsData);
    },
    memberProgressPercent(member) {
      const target = hoursToDecimal(member.targetHours);
      if (target <= 0) return 0;
      const pct = (hoursToDecimal(member.loggedHours) / target) * 100;
      return Math.round(pct);
    },
  },
};
